import { SpotlightIntro, SpotlightExplore, SpotlightRailLayout } from "./spotlight/SpotlightCourses";
import { PersonListSkeleton, LeaderboardSkeleton, EventListSkeleton, RewardGridSkeleton } from "./skeletons";
import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { toEmbed } from "./toEmbed";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
import { cachedPost } from "./apiCache";
import { railBody, RAIL_PARTS } from "./railData";
import { onPointsChanged, emitPointsChanged } from "./pointsBus";
import InlineBanner from "./InlineBanner";
import LockIllustration from "./LockIllustration";
import CourseOfferModal, { offerMoney, cheapestOffer } from "./CourseOfferModal";
import { asset } from "./assetUrl";
import EmptyState from "./EmptyState";
import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";
import { withReferral } from "./affiliateReferral";

// Premium default cover for events with no image — a vibrant live-event crowd.
// Per event-type default cover the admin shows when a creator sets no image
// (fleximembership public/images/event-default-*.png), copied into this app's
// public/images so the learner app renders the same placeholder. Keyed by
// event_type: 1 Google Meet · 2 Zoom · 3 Webinar · 4 Live Event.
// Routed through asset(): a bare /images/... resolves against the CUSTOMER's
// domain, which answers 405, not against the origin this bundle is served from.
const EVENT_TYPE_IMAGE = {
  1: asset("/images/event-default-meet.png"),
  2: asset("/images/event-default-zoom.png"),
  3: asset("/images/event-default-webinar.png"),
  4: asset("/images/event-default-live.png"),
};
// The cover to show for an event: its own image, else its type's default,
// falling back to the Live default for any unknown type.
const eventImage = (e) => e?.image || EVENT_TYPE_IMAGE[Number(e?.event_type)] || EVENT_TYPE_IMAGE[4];

// Human label for each event type (matches the admin's EVENT_TYPES).
const EVENT_TYPE_LABEL = { 1: "Google Meet", 2: "Zoom", 3: "Webinar", 4: "Live Event" };
const eventTypeLabel = (t) => EVENT_TYPE_LABEL[Number(t)] || "Event";

// Default artwork shown when a reward / badge has no uploaded image. Inline SVG
// data-URIs so they always render (no network dependency, never 404): a gift box
// for rewards, a gold medal-star for badges.
const DEFAULT_REWARD_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23111827'/%3E%3Cg fill='none' stroke='%239ca3af' stroke-width='14' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='120' y='192' width='160' height='118' rx='12'/%3E%3Cpath d='M112 192h176M200 192v118M200 192c-18-38-66-38-66-8 0 18 38 8 66 8zM200 192c18-38 66-38 66-8 0 18-38 8-66 8z'/%3E%3C/g%3E%3C/svg%3E";
const DEFAULT_BADGE_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cdefs%3E%3ClinearGradient id='bg' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23fbbf24'/%3E%3Cstop offset='1' stop-color='%23f59e0b'/%3E%3C/linearGradient%3E%3C/defs%3E%3Ccircle cx='100' cy='100' r='72' fill='url(%23bg)'/%3E%3Cpath d='M100 58l12 25 27 3-20 19 5 27-24-13-24 13 5-27-20-19 27-3z' fill='white'/%3E%3C/svg%3E";

function Avatar({ name, img, primary, size = 44 }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full overflow-hidden flex items-center justify-center font-bold shrink-0"
      style={
        img
          ? { width: size, height: size }
          : { width: size, height: size, backgroundColor: `${primary}1f`, color: primary, border: `1px solid ${primary}40`, fontSize: size * 0.4 }
      }
    >
      {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : initial}
    </div>
  );
}

// Medal icon for top-3 ranks (gold / silver / bronze) — replaces the emoji.
const MedalIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 3l2.5 5M15.5 3L13 8" />
    <circle cx="12" cy="15" r="6" />
    <path d="M12 12.6l.9 1.85 2.04.3-1.47 1.43.35 2.03L12 17.3l-1.82.96.35-2.03-1.47-1.43 2.04-.3.9-1.85z" fill="currentColor" stroke="none" />
  </svg>
);

// "2026-04-12 06:31:52" -> "Apr 2026"
function fmtAchieved(d) {
  if (!d) return "";
  const dt = new Date(String(d).replace(" ", "T"));
  if (isNaN(dt)) return "";
  return dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// Small trophy glyph used next to achievement dates.
const TrophyMini = ({ className = "w-3 h-3" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
  </svg>
);

// Small padlock glyph for not-yet-reached levels.
const LockMini = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V8a5 5 0 0110 0v3M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
  </svg>
);

function useFetch(endpoint, token, groupToken, key) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/${endpoint}`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (active && res.data?.error === 0) setData(res.data[key] || []);
      })
      .catch(() => active && setData([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [endpoint, token, groupToken, key]);
  return { loading, data };
}

const GROUP_ROLE_LABEL = { 0: "Member", 1: "Admin", 2: "Moderator" };

/**
 * Group-admin management panel — pending approvals (A1), role changes (A2),
 * and member removal (A3). Self-hides unless the signed-in member is an admin
 * (role=1) of the group: the membership-group-members endpoint is admin-gated,
 * so a non-admin / non-group simply gets an error and we render nothing.
 */
function GroupManage({ token, groupToken, primary, member }) {
  const [members, setMembers] = useState(null); // null = loading, [] or list when admin, false = not admin
  const [busy, setBusy] = useState(0);
  const [group, setGroup] = useState(null);
  const [edit, setEdit] = useState(null);   // form object when the edit panel is open
  const [saving, setSaving] = useState(false);

  const body = useCallback((extra = {}) => ({
    community_token: token,
    group_token: groupToken,
    community_member_id: member?.community_member_id,
    member_token: member?.auth_token,
    ...extra,
  }), [token, groupToken, member?.community_member_id, member?.auth_token]);

  const load = useCallback(async () => {
    if (!groupToken || !member?.auth_token) { setMembers(false); return; }
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-group-members`, body());
      if (res.data?.error === 0) { setMembers(res.data.members || []); setGroup(res.data.group || null); }
      else setMembers(false);
    } catch {
      setMembers(false); // 403 / not an admin → hide the panel
    }
  }, [groupToken, member?.auth_token, body]);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => setEdit({
    name: group?.name || "",
    description: group?.description || "",
    access_type: group?.access_type || 1,
    approve_members: group?.settings?.approve_members ? 1 : 0,
    quota_status: group?.settings?.quota_status ? 1 : 0,
    quota_limit: group?.settings?.quota_limit || 0,
    waitlist_enabled: group?.settings?.waitlist_enabled ? 1 : 0,
  });
  const saveGroup = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-group-update`, body(edit));
      if (res.data?.error === 0) { toast.success("Community updated"); setEdit(null); await load(); }
      else toast.error(res.data?.msg || "Could not update community.");
    } catch { toast.error("Could not update community."); }
    finally { setSaving(false); }
  };

  if (members === null || members === false) return null;

  const pending = members.filter((m) => m.status === 0);
  const active = members.filter((m) => m.status === 1);
  const waitlist = members.filter((m) => m.status === 3);

  const act = async (path, extra, okMsg) => {
    setBusy(extra.member_id);
    try {
      const res = await axios.post(`${config.COMMUNITY_API_URL}/${path}`, body(extra));
      if (res.data?.error === 0) { if (okMsg) toast.success(okMsg); await load(); }
      else toast.error(res.data?.msg || "Action failed.");
    } catch { toast.error("Action failed."); }
    finally { setBusy(0); }
  };

  return (
    <div className="mb-5 bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2" style={{ background: `${primary}0d` }}>
        <span className="text-[13px] font-extrabold uppercase tracking-wide" style={{ color: primary }}>Manage community</span>
        <span className="text-[11px] font-semibold text-gray-400">{active.length} members · {pending.length} pending</span>
        {group && (
          <button onClick={() => (edit ? setEdit(null) : openEdit())} className="ml-auto rounded-lg px-2.5 py-1 text-[11px] font-bold ring-1 ring-gray-200 text-gray-600 transition hover:bg-gray-50">
            {edit ? "Close" : "Edit community"}
          </button>
        )}
      </div>

      {edit && (
        <div className="p-4 border-b border-gray-100 space-y-3 bg-gray-50/40">
          <div>
            <label className="text-[11px] font-bold text-gray-500">Community name</label>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-gray-300" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-gray-500">Description</label>
            <textarea rows={2} value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} className="mt-1 w-full resize-none rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-gray-300" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <input type="checkbox" checked={!!edit.approve_members} onChange={(e) => setEdit({ ...edit, approve_members: e.target.checked ? 1 : 0 })} /> Require approval to join
            </label>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <input type="checkbox" checked={edit.access_type === 2} onChange={(e) => setEdit({ ...edit, access_type: e.target.checked ? 2 : 1 })} /> Private community
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
              <input type="checkbox" checked={!!edit.quota_status} onChange={(e) => setEdit({ ...edit, quota_status: e.target.checked ? 1 : 0 })} /> Limit members
            </label>
            {!!edit.quota_status && (
              <input type="number" min="0" value={edit.quota_limit} onChange={(e) => setEdit({ ...edit, quota_limit: Number(e.target.value) })} className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-sm outline-none" placeholder="Max" />
            )}
            {!!edit.quota_status && (
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                <input type="checkbox" checked={!!edit.waitlist_enabled} onChange={(e) => setEdit({ ...edit, waitlist_enabled: e.target.checked ? 1 : 0 })} /> Waitlist when full
              </label>
            )}
          </div>
          <button onClick={saveGroup} disabled={saving || !edit.name.trim()} className="rounded-lg px-4 py-1.5 text-sm font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-50" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}

      {pending.length > 0 && (
        <div className="p-4 border-b border-gray-100">
          <p className="mb-2 text-xs font-bold text-gray-700">Pending requests</p>
          <ul className="space-y-2">
            {pending.map((m) => (
              <li key={m.member_id} className="flex items-center gap-3 rounded-xl border border-amber-100 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 p-2.5">
                <Avatar name={m.name} img={m.img} primary={primary} size={36} />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-gray-800">{m.name}</p><p className="truncate text-[11px] text-gray-400">{m.email}</p></div>
                <button disabled={busy === m.member_id} onClick={() => act("membership-group-approve", { member_id: m.member_id, action: "approve" }, "Member approved")} className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-50" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>Approve</button>
                <button disabled={busy === m.member_id} onClick={() => act("membership-group-approve", { member_id: m.member_id, action: "reject" }, "Request rejected")} className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50">Reject</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {waitlist.length > 0 && (
        <div className="p-4 border-b border-gray-100">
          <p className="mb-2 text-xs font-bold text-gray-700">Waitlist <span className="text-gray-400">({waitlist.length})</span></p>
          <ul className="space-y-2">
            {waitlist.map((m) => (
              <li key={m.member_id} className="flex items-center gap-3 rounded-xl border border-sky-100 dark:border-sky-500/30 bg-sky-50/60 dark:bg-sky-500/10 p-2.5">
                <Avatar name={m.name} img={m.img} primary={primary} size={36} />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-gray-800">{m.name}</p><p className="truncate text-[11px] text-gray-400">{m.email}</p></div>
                <button disabled={busy === m.member_id} onClick={() => act("membership-group-approve", { member_id: m.member_id, action: "approve" }, "Promoted from waitlist")} className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-50" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>Promote</button>
                <button disabled={busy === m.member_id} onClick={() => act("membership-group-approve", { member_id: m.member_id, action: "reject" }, "Removed from waitlist")} className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-50">Remove</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="p-4">
        <p className="mb-2 text-xs font-bold text-gray-700">Members &amp; roles</p>
        <ul className="space-y-2">
          {active.map((m) => {
            const isSelf = m.member_id === member?.community_member_id;
            return (
              <li key={m.member_id} className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/50 p-2.5">
                <Avatar name={m.name} img={m.img} primary={primary} size={36} />
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-gray-800">{m.name}{isSelf ? " (you)" : ""}</p><p className="truncate text-[11px] text-gray-400">{m.email}</p></div>
                {isSelf ? (
                  <span className="shrink-0 text-[11px] font-bold" style={{ color: primary }}>{GROUP_ROLE_LABEL[m.role]}</span>
                ) : (
                  <>
                    <select value={m.role} disabled={busy === m.member_id} onChange={(e) => act("membership-group-member-role", { member_id: m.member_id, role: Number(e.target.value) }, "Role updated")} className="shrink-0 rounded-lg border border-gray-200 px-2 py-1 text-xs font-semibold text-gray-700 outline-none">
                      <option value={0}>Member</option>
                      <option value={2}>Moderator</option>
                      <option value={1}>Admin</option>
                    </select>
                    <button disabled={busy === m.member_id} onClick={() => act("membership-group-member-remove", { member_id: m.member_id }, "Member removed")} title="Remove from community" className="shrink-0 rounded-md p-1.5 text-gray-300 dark:text-gray-500 transition hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 disabled:opacity-50">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0v12a1 1 0 001 1h6a1 1 0 001-1V7" /></svg>
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/* Group filter dropdown — lists only the groups the signed-in member can access
   (plus an "All members" option). Mirrors the app's custom-dropdown pattern. */
export function MemberGroupFilter({ groups, value, onChange, primary, totalAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, []);
  const options = [{ group_token: "", name: "All members", member_count: totalAll }, ...groups];
  const current = options.find((o) => o.group_token === value) || options[0];
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-surface px-3.5 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
      >
        <svg className="h-4 w-4 shrink-0" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h18M6 12h12M10 19h4" />
        </svg>
        <span className="truncate max-w-[180px]">{current.name}</span>
        <span className="text-xs font-bold text-gray-400">{current.member_count}</span>
        <svg className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-surface p-1.5 shadow-xl ring-1 ring-black/5">
          {options.map((o) => {
            const active = o.group_token === value;
            return (
              <button
                key={o.group_token || "all"}
                type="button"
                onClick={() => { onChange(o.group_token); setOpen(false); }}
                className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-sm font-semibold transition"
                style={active ? { background: `${primary}0f`, color: primary } : { color: "rgb(var(--c-gray-700))" }}
              >
                <span className="flex-1 truncate text-left">{o.name}</span>
                <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500">{o.member_count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Generic filter dropdown for the Members page (Course / Group). options:
   [{ key, name, member_count }]; value = selected key ("" = the All row). */
function FilterSelect({ label, allLabel, placeholder, options, value, onChange, primary }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left, width }
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // Close on click outside BOTH the button and the (portaled) panel.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    addOutsidePress(h);
    return () => removeOutsidePress(h);
  }, [open]);

  // The panel is portaled with fixed positioning so the Members card's
  // overflow-hidden never clips it. Right-aligned to the button, clamped to
  // the viewport, flipped above when there's no room below.
  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const W = 240, MARGIN = 8;
    const panelH = panelRef.current?.offsetHeight || 300;
    let left = Math.min(r.right - W, window.innerWidth - W - MARGIN);
    left = Math.max(MARGIN, left);
    const below = window.innerHeight - r.bottom;
    const openUp = below < panelH + MARGIN && r.top > below;
    let top = openUp ? r.top - panelH - 8 : r.bottom + 8;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - panelH - MARGIN));
    setPos({ top, left, width: W });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // `allLabel` adds an "All …" reset row; when omitted the dropdown lists only
  // real options and shows `placeholder` until one is picked.
  const opts = allLabel ? [{ key: "", name: allLabel }, ...options] : options;
  const current = opts.find((o) => o.key === value);
  const displayName = current ? current.name : (placeholder || "Select");
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-surface px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
        style={value ? { borderColor: primary, color: primary } : undefined}
      >
        {label && <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</span>}
        <span className={`max-w-[130px] truncate ${current ? "" : "text-gray-400"}`}>{displayName}</span>
        <svg className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: pos ? pos.top : -9999,
              left: pos ? pos.left : -9999,
              width: pos ? pos.width : 240,
              visibility: pos ? "visible" : "hidden",
            }}
            className="z-[10000] max-h-72 overflow-y-auto rounded-2xl border border-gray-200 bg-surface p-1.5 shadow-xl ring-1 ring-black/5"
          >
            {opts.length === 0 && (
              <div className="px-2.5 py-4 text-center text-xs text-gray-400">Nothing here yet</div>
            )}
            {opts.map((o) => {
              const active = o.key === value;
              return (
                <button
                  key={o.key || "all"}
                  type="button"
                  onClick={() => { onChange(o.key); setOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2.5 text-sm font-semibold transition"
                  style={active ? { background: `${primary}0f`, color: primary } : { color: "rgb(var(--c-gray-700))" }}
                >
                  <span className="flex-1 truncate text-left">{o.name}</span>
                  {typeof o.member_count === "number" && (
                    <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500">{o.member_count}</span>
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

// Members per page. The API hard-caps per_page at 500, so this must stay at or
// under that — a larger number is silently clamped server-side and the pager
// would then skip whoever fell outside the clamp.
const MEMBERS_PER_PAGE = 15;

export function MembersView({ token, primary, member, groupCard, pointsCard, topBanner }) {
  const myId = member?.community_member_id;
  // Membership-first list with two optional, mutually-exclusive filters:
  //   courseFilter = a course's funnel_page_id (members who started that course)
  //   groupFilter  = a group_token          (members who joined that group)
  // NOTE: the course filter is UI-only for now — the backend wiring is being
  // reworked, so selecting a course currently returns the membership-wide list.
  // Deep-link from a group card's "N Members" link: ?group=<group_token> opens
  // the Groups filter with that group preselected.
  const initialGroup = new URLSearchParams(window.location.search).get("group") || "";
  const [courseFilter, setCourseFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState(initialGroup);
  // Which list the single filter dropdown shows: "courses" | "groups".
  const [mode, setMode] = useState(initialGroup ? "groups" : "courses");
  /*
   * Name search. `search` is what the box holds; `query` is what has actually
   * been sent, debounced so typing does not fire a request per keystroke.
   *
   * Searched server-side, like the paging: the list only holds one page, so
   * filtering the loaded rows would silently miss every matching member who
   * happens to be on another page.
   */
  // Seeded from ?q= so a member found through search opens pre-filtered.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  /*
   * Server-side paging.
   *
   * The endpoint caps a page at 500 rows. The previous code asked for one page,
   * kept only the rows, and printed members.length as the member count — so a
   * membership past 500 members read "500 members" for ever (while the filter
   * dropdown, which uses a real COUNT, correctly said 980), and members beyond
   * the first 500 could not be reached at all.
   *
   * `total` is a true COUNT from the API and drives both the badge and the
   * pager; the rows are just the page being shown.
   */
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ members: [], courses: [], groups: [], access: true, courseGate: null, loading: true, total: 0 });
  // The gate whose offer chooser is open, when the locked course turns out to be
  // sold by more than one product. Null the rest of the time.
  const [offerGate, setOfferGate] = useState(null);
  const navigate = useNavigate();

  // Changing a filter or the search changes the result set, so the old page
  // number no longer means anything — page 7 of the membership is rarely page 7
  // of one group, and almost never page 7 of a search.
  // The chooser belongs to the gate that opened it, so changing what is being
  // looked at closes it — otherwise it would reappear over the next locked
  // course, listing the previous one's plans.
  useEffect(() => { setPage(1); setOfferGate(null); }, [courseFilter, groupFilter, query]);

  useEffect(() => {
    // Abort the previous request rather than only ignoring its result.
    //
    // The cleanup used to just flip `active = false`: the superseded request
    // kept running to completion, holding one of the browser's six connections
    // to this host. Paging quickly left several in flight at once, so a later
    // page could sit QUEUED behind requests whose answers nobody wanted — the
    // spinner with no network activity to explain it.
    const controller = new AbortController();
    let active = true;
    setState((s) => ({ ...s, loading: true }));
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-members`, {
        community_token: token,
        community_member_id: myId || undefined,
        course_page_id: courseFilter || undefined,
        group_token: groupFilter || undefined,
        q: query || undefined,
        page,
        per_page: MEMBERS_PER_PAGE,
      }, { signal: controller.signal })
      .then((res) => {
        if (!active) return;
        if (res.data?.error === 0) {
          const rows = res.data.members || [];
          setState({
            members: rows,
            courses: res.data.courses || [],
            groups: res.data.groups || [],
            access: res.data.access !== false,
            courseGate: res.data.course_gate || null,
            loading: false,
            // Older API builds predate `total`; fall back to the page length so
            // the badge shows something sane rather than 0.
            total: Number(res.data.total ?? rows.length),
          });
        } else {
          setState((s) => ({ ...s, members: [], access: true, courseGate: null, loading: false, total: 0 }));
        }
      })
      .catch((err) => {
        // A superseded run must NOT clear the spinner — the newer run owns it,
        // and clearing here would flash an empty list under the new request.
        if (!active || axios.isCancel?.(err) || err?.name === "CanceledError") return;
        setState((s) => ({ ...s, members: [], access: true, courseGate: null, loading: false, total: 0 }));
      });
    return () => { active = false; controller.abort(); };
  }, [token, myId, courseFilter, groupFilter, query, page]);

  const { members, courses, groups, access, courseGate, loading, total } = state;
  const pageCount = Math.max(1, Math.ceil(total / MEMBERS_PER_PAGE));
  // A filter change can land the viewer past the end of a shorter result set;
  // clamp for display and let the effect below pull them back.
  const pageClamped = Math.min(page, pageCount);
  const rangeStart = total === 0 ? 0 : (pageClamped - 1) * MEMBERS_PER_PAGE + 1;
  const rangeEnd = (pageClamped - 1) * MEMBERS_PER_PAGE + members.length;
  // If the result set shrank under the viewer (a member removed, a filter
  // applied), the page they are on can stop existing. Pull them to the last
  // real page instead of leaving them on an empty list next to a pager that
  // says there are members.
  useEffect(() => {
    if (!loading && page > pageCount) setPage(pageCount);
  }, [loading, page, pageCount]);
  const activeCourse = courses.find((c) => String(c.course_page_id) === courseFilter);
  const activeGroup = groups.find((g) => g.group_token === groupFilter);
  // No access to the selected course/group → show a Get access / Join prompt.
  const gated = !!(courseFilter || groupFilter) && !loading && access === false;
  const goToCourse = () => navigate(`/membership/${token}/courses?course=${courseFilter}`);
  const goToAccess = () => {
    if (groupFilter) navigate(`/membership/${token}/groups?gid=${groupFilter}`);
    else if (courseFilter) goToCourse();
  };
  // A PAID course sends them to its published sales page to buy it; a LEVEL
  // course has nothing to buy, so it sends them to the course itself, where the
  // card and landing page spell out the level they need. A paid course whose
  // product has no published sales page falls back to the same place rather
  // than opening an empty tab.
  // A paid course the member does not hold must NEVER fall through to
  // goToCourse() — that opens the very course they were told to purchase. The
  // fallback exists for the LEVEL gate, which has nothing to buy and sends them
  // to the course to read how to unlock it; a paid course with no resolvable
  // sales page has nowhere legitimate to go, so it offers no action at all
  // (see gateHasAction — the button is not rendered).
  const paidGate = courseGate?.access_type === "paid";
  // Several products sell this course, each with its own page and price. The gate
  // asks which one rather than picking for them — the same chooser the course
  // grid opens. Empty for a single-product course, so nothing changes there.
  const gateOffers = paidGate && Array.isArray(courseGate?.offers) ? courseGate.offers : [];
  const gateHasChoice = gateOffers.length > 1;
  const gateHasAction = paidGate ? !!courseGate?.sales_url : !!courseGate;
  const takeGateAction = () => {
    if (paidGate) {
      if (gateHasChoice) {
        setOfferGate(courseGate);
        return;
      }
      // New tab, matching the other two places a course sends a member to its
      // sales page (the course-grid card and the instructor page). This one used
      // to navigate in place, which threw away where they were — they were part
      // way through browsing members of a course and came back to the top of the
      // app. noopener/noreferrer because the destination is a creator-supplied URL.
      if (courseGate.sales_url) window.open(withReferral(courseGate.sales_url), "_blank", "noopener,noreferrer");
      return;
    }
    goToCourse();
  };
  /*
   * The headline price on the gate panel.
   *
   * With ONE offer this is the course's own stored price, as it always was. With
   * several it reads "From <cheapest>" instead: courseGate.price comes from
   * course_access_data, which belongs to whichever product was wired to the course
   * first, so on a multi-product course it quoted one plan's price beside a button
   * that could lead to any of them.
   */
  const gateCheapest = gateHasChoice ? cheapestOffer(gateOffers) : null;
  const gatePrice = gateCheapest
    ? `From ${offerMoney(gateCheapest.price, gateCheapest.currency)}`
    : courseGate?.access_type === "paid" && Number(courseGate.price) > 0
      // Same formatter as the plan chooser (₹1,499, not a bare "1499").
      ? offerMoney(courseGate.price, courseGate.currency)
      : "";
  const gateLevelName = courseGate?.unlock_level_name || (courseGate?.unlock_level ? `Level ${courseGate.unlock_level}` : "");
  // Both gates take the membership's brand colour. A level gate was amber here
  // to echo the LEVEL badge on the course card, but this panel fills the page
  // beside the themed points/levels rail — at that size the badge's accent
  // stopped reading as a label and started reading as the section's own colour,
  // fighting the theme. The card badge keeps its amber; it's small enough to.
  //
  // One accent drives the illustration, the pill, the meter and the button, so
  // the panel reads as one object rather than a stack of separate parts.
  const gateAccent = primary;
  // How far along the points ladder they are. Clamped to 4% so a member on zero
  // still sees the track has a direction, and to 100% at the top.
  const gateProgressPct = (() => {
    const need = Number(courseGate?.unlock_points) || 0;
    if (need <= 0) return 0;
    const have = Number(courseGate?.member_points) || 0;
    return Math.max(4, Math.min(100, Math.round((have / need) * 100)));
  })();
  const subtitle = activeCourse
    ? `Members in ${activeCourse.name}`
    : activeGroup
      ? `Members in ${activeGroup.name}`
      : "Everyone in this membership";
  // The search counts as a filter for the empty state, so "no results" offers a
  // way back instead of reading as "this membership has no members".
  const hasFilter = !!(courseFilter || groupFilter || query);
  // A level-gated course isn't "started" — it opens once a member earns the
  // level, so nobody being listed means nobody has reached it yet. Saying
  // "no one's started this course" there points the reader at the wrong thing.
  const isLevelCourse = activeCourse?.access_type === "level";
  // A search that matched nothing is not the same as an empty membership, and
  // must not be reported as one — it takes priority over the other wordings.
  const emptyTitle = query
    ? "No members match that name"
    : activeCourse
    ? (isLevelCourse ? "No one has unlocked this course yet" : "No one's started this course yet")
    : activeGroup
      ? "No members in this community yet"
      : "No members yet";
  const emptyHint = query
    ? `Nobody here matches “${query}”. Check the spelling, or clear the search to see everyone.`
    : activeCourse
    ? (isLevelCourse
        ? `Members who reach the level that unlocks “${activeCourse.name}” will appear here.`
        : `Members who start “${activeCourse.name}” will appear here.`)
    : activeGroup
    ? `Members who join “${activeGroup.name}” will appear here.`
    : "Members of this membership will show up here.";

  return (
    <div className="tpl-rail-grid">
      {/* Members list — main column (left) */}
      <div className="order-1">
        {topBanner}
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-4">
            <div>
              <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
                <svg className="h-5 w-5" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                Members
              </h2>
              <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Search by member name. Sent to the API (debounced), not applied
                  to the loaded rows — the list is one page, so filtering here
                  would miss matches sitting on any other page. */}
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search members"
                  aria-label="Search members by name"
                  className="w-44 rounded-xl border border-gray-200 bg-surface py-2 pl-9 pr-8 text-sm text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-gray-300 sm:w-52"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Courses | Groups toggle — picks which list the dropdown shows. */}
              <div className="inline-flex items-center rounded-xl bg-gray-100 p-0.5">
                {[["courses", "Courses"], ["groups", "Communities"]].map(([key, lab]) => {
                  const on = mode === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setMode(key); setCourseFilter(""); setGroupFilter(""); }}
                      className={`rounded-lg px-3.5 py-1.5 text-sm font-bold transition ${on ? "bg-surface shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                      style={on ? { color: primary } : undefined}
                    >
                      {lab}
                    </button>
                  );
                })}
              </div>
              {/* Single dropdown for the active mode — no "All" option. */}
              {mode === "courses" ? (
                <FilterSelect
                  placeholder="Select course"
                  options={courses.map((c) => ({ key: String(c.course_page_id), name: c.name, member_count: c.member_count }))}
                  value={courseFilter}
                  onChange={(v) => { setCourseFilter(v); setGroupFilter(""); }}
                  primary={primary}
                />
              ) : (
                <FilterSelect
                  placeholder="Select community"
                  options={groups.map((g) => ({ key: g.group_token, name: g.name, member_count: g.member_count }))}
                  value={groupFilter}
                  onChange={(v) => { setGroupFilter(v); setCourseFilter(""); }}
                  primary={primary}
                />
              )}
              {/* Clear the active filter. The search has its own ✕ inside the
                  box, so this only appears for a course/group selection —
                  otherwise typing a name would grow a second Clear button. */}
              {(courseFilter || groupFilter) && (
                <button
                  type="button"
                  onClick={() => { setCourseFilter(""); setGroupFilter(""); }}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-surface px-3 py-2 text-sm font-semibold text-gray-500 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              )}
              {/* Member count badge — hidden when access is gated. */}
              {!gated && (
                <span
                  className="inline-flex shrink-0 items-center rounded-full px-3 py-1.5 text-sm font-bold"
                  style={{ backgroundColor: `${primary}14`, color: primary }}
                >
                  {/* `total` (a real COUNT from the API), never members.length —
                      that is only the rows on this page and caps at the page size. */}
                  {loading ? "…" : total} {total === 1 ? "member" : "members"}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <PersonListSkeleton count={8} />
          ) : gated && courseGate ? (
            // The viewer can't open the selected course. Same lock illustration
            // the course page itself uses, so hitting the gate from either
            // surface reads as the same wall — tinted with the membership's
            // brand colour, like the points and levels rail beside it.
            <div className="relative flex min-h-[62vh] items-center justify-center overflow-hidden bg-gradient-to-b from-gray-50 to-surface px-6 py-14">
              {/* Soft accent wash behind the art — depth without a hard panel edge. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-64 opacity-70"
                style={{ background: `radial-gradient(60% 100% at 50% 0%, ${gateAccent}1f 0%, transparent 70%)` }}
              />

              <div className="anim-modal relative flex w-full max-w-lg flex-col items-center text-center">
                <LockIllustration accent={gateAccent} className="h-40 w-full max-w-[240px] sm:h-48" />

                {/* Status pill — names the gate before the eye reaches the copy. */}
                <span
                  className="mt-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]"
                  style={{ backgroundColor: `${gateAccent}14`, color: gateAccent }}
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                  {courseGate.access_type === "paid" ? "Locked · Paid" : "Locked · Level"}
                </span>

                {/* The line that matters, sized to be read from across the page. */}
                <p className="mt-4 max-w-md text-balance text-[22px] font-semibold leading-snug tracking-[-0.01em] text-gray-900 sm:text-[26px]">
                  {courseGate.access_type === "paid"
                    ? "Purchase this course to see who's inside."
                    : gateLevelName
                      ? `Reach ${gateLevelName}${courseGate.unlock_points > 0 ? ` (${courseGate.unlock_points} pts)` : ""} to unlock this course.`
                      : "Reach a higher level to unlock this course."}
                </p>

                {/* The course being gated — name over its own description. */}
                <p className="mt-5 max-w-sm truncate text-[12px] font-semibold uppercase tracking-[0.1em] text-gray-400">
                  {courseGate.name}
                </p>
                {courseGate.description && (
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-gray-500">{courseGate.description}</p>
                )}

                {/* Progress toward the gate — a level gate is a distance, and
                    showing it turns "locked" into something with an end in sight.
                    A paid gate has no distance, just a price. */}
                {courseGate.access_type === "paid" ? (
                  gatePrice && (
                    <p className="mt-6 text-[28px] font-extrabold tracking-tight" style={{ color: gateAccent }}>
                      {gatePrice}
                    </p>
                  )
                ) : courseGate.unlock_points > 0 ? (
                  <div className="mt-6 w-full max-w-xs">
                    <div className="mb-1.5 flex items-baseline justify-between text-[11px] font-bold">
                      <span className="text-gray-400">{courseGate.member_points} pts</span>
                      <span style={{ color: gateAccent }}>
                        {courseGate.points_needed > 0 ? `${courseGate.points_needed} pts to go` : "Unlocked"}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/70">
                      <div
                        className="h-full rounded-full transition-[width] duration-700 ease-out"
                        style={{
                          width: `${gateProgressPct}%`,
                          background: `linear-gradient(90deg, ${gateAccent}b3 0%, ${gateAccent} 100%)`,
                          boxShadow: gateProgressPct > 0 ? `0 1px 6px ${gateAccent}66` : "none",
                        }}
                      />
                    </div>
                  </div>
                ) : null}

                {offerGate ? (
                  <CourseOfferModal
                    courseName={offerGate.name}
                    offers={offerGate.offers}
                    primary={primary}
                    onClose={() => setOfferGate(null)}
                  />
                ) : null}

                {gateHasAction ? (
                  <button
                    type="button"
                    onClick={takeGateAction}
                    className="mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-7 py-3.5 text-[14.5px] font-bold transition-all duration-200 hover:brightness-[1.06] active:scale-[.98] sm:w-auto"
                    style={{
                      background: `linear-gradient(140deg, ${gateAccent}, ${gateAccent}c4)`,
                      color: "var(--brand-btn-text, #fff)",
                      boxShadow: `0 14px 28px -14px ${gateAccent}`,
                    }}
                  >
                    {courseGate.access_type !== "paid"
                      ? "See how to unlock"
                      // "Buy now" promises one thing to buy; with several plans
                      // behind it the button opens a chooser, so it says so.
                      : gateHasChoice ? "Choose a plan" : "Buy now"}
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h11" />
                    </svg>
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={() => { setCourseFilter(""); setGroupFilter(""); }}
                  className={`${gateHasAction ? "mt-3" : "mt-8"} text-[13px] font-semibold text-gray-400 transition hover:text-gray-600`}
                >
                  Show all members
                </button>
              </div>
            </div>
          ) : gated ? (
            // No access to the selected group — prompt to join.
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span
                className="flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${primary}12`, color: primary }}
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </span>
              <h3 className="mt-4 text-base font-bold text-gray-800">
                You don't have access to this {groupFilter ? "community" : "course"}
              </h3>
              <p className="mt-1 max-w-xs text-sm leading-relaxed text-gray-400">
                {groupFilter
                  ? `Join “${activeGroup?.name || "this community"}” to see who's inside.`
                  : `Get access to “${activeCourse?.name || "this course"}” to see who's inside.`}
              </p>
              <button
                type="button"
                onClick={goToAccess}
                className="mt-5 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:brightness-110"
                style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
              >
                {groupFilter ? "Join community" : "Get access"}
              </button>
            </div>
          ) : members.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span
                className="flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${primary}12`, color: primary }}
              >
                <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              </span>
              <h3 className="mt-4 text-base font-bold text-gray-800">{emptyTitle}</h3>
              <p className="mt-1 max-w-xs text-sm leading-relaxed text-gray-400">{emptyHint}</p>
              {hasFilter && (
                <button
                  type="button"
                  // Clears the search too, or the button would promise "all
                  // members" and hand back the same empty list.
                  onClick={() => { setCourseFilter(""); setGroupFilter(""); setSearch(""); }}
                  className="mt-5 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:brightness-110"
                  style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                >
                  Show all members
                </button>
              )}
            </div>
          ) : (
            <ul className="space-y-2.5 bg-gray-50/40 p-4">
              {members.map((m) => {
                return (
                  <li key={m.member_token}>
                    <div
                      className="flex w-full items-center gap-3.5 rounded-xl border bg-surface p-3 text-left shadow-sm transition hover:border-gray-200 hover:shadow-md"
                      style={{ borderColor: "rgb(var(--c-gray-100))" }}
                    >
                      <span className="shrink-0 rounded-full p-[2px]" style={{ background: `${primary}26` }}>
                        <span className="block rounded-full ring-2 ring-surface">
                          <Avatar name={m.name} img={m.img} primary={primary} size={44} />
                        </span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-1.5 truncate text-[15px] font-bold text-gray-800">
                          <span className="truncate">{m.name}</span>
                          {m.is_admin && (
                            <span className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wide" style={{ backgroundColor: `${primary}1a`, color: primary }}>Admin</span>
                          )}
                          {m.is_self && (
                            <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-gray-500">You</span>
                          )}
                        </p>
                        <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-400">
                          {m.location ? (
                            <>
                              <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17.66 16.66L13.41 20.9a2 2 0 01-2.82 0l-4.25-4.24a8 8 0 1111.32 0z" /></svg>
                              <span className="truncate">{m.location}</span>
                            </>
                          ) : (
                            <span className="truncate">Joined {m.joined_on || "—"}</span>
                          )}
                        </p>
                      </div>
                      {/* The per-member "Chat" button was moved to the dedicated
                          "Members Chat" nav section (its inbox + contact picker
                          starts new 1:1 conversations). */}
                      <span className="shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
                        {m.points} pts
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Pager — only once there is more than one page. Members past the
              first page were previously unreachable: the list asked for one
              page and never offered a way to the rest. */}
          {!loading && !gated && pageCount > 1 && (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 sm:flex-row">
              <p className="text-xs text-gray-400">
                Showing <span className="font-semibold text-gray-600">{rangeStart}</span>
                –<span className="font-semibold text-gray-600">{rangeEnd}</span>{" "}
                of <span className="font-semibold text-gray-600">{total}</span> members
              </p>
              <nav className="flex items-center gap-1.5" aria-label="Members pagination">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pageClamped <= 1}
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-200 bg-surface px-3 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="px-2 text-xs font-semibold text-gray-500">
                  Page {pageClamped} of {pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={pageClamped >= pageCount}
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-200 bg-surface px-3 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* Right rail — community banner + My Points (matches the Courses page) */}
      <aside className="order-2 space-y-4 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
        {groupCard}
        {pointsCard}
        {/* Banner ads for the Members page — under the points card. */}
        <InlineBanner token={token} placement="members" wrapperClass="space-y-3" />
      </aside>
    </div>
  );
}

export function ReferralLink({ token, groupToken, urlToken, primary, points }) {
  const [copied, setCopied] = useState(false);
  // Viral Share / referral disabled — feature commented out per request.
  // Component renders nothing so no page/template surfaces the referral link.
  const referralDisabled = true;
  // Share a deep link straight into this group's feed (guest-viewable) carrying
  // the referrer's code: /:token?gid={group}&page=feed&ref={urlToken}. Falls back
  // to the community home link when there's no group in context. Preserve the
  // /membership/:token/login shape if that's how the member arrived.
  const base = window.location.pathname.startsWith("/membership/")
    ? `${window.location.origin}/membership/${token}/login`
    : `${window.location.origin}/${token}`;
  const link = groupToken
    ? `${base}?gid=${groupToken}&page=feed&ref=${urlToken}`
    : `${base}?ref=${urlToken}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.createElement("textarea");
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (referralDisabled) return null;

  return (
    <div className="mt-5 pt-5 border-t border-gray-100">
      <div
        className="rounded-xl p-3.5"
        style={{ backgroundColor: `${primary}0d`, border: `1px solid ${primary}1f` }}
      >
        {/* Header — icon chip + title + reward badge */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0"
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </span>
          <p className="text-sm font-bold text-gray-900">Referral Link</p>
          {points > 0 && (
            <span
              className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ backgroundColor: `${primary}1f`, color: primary }}
            >
              +{points} pts
            </span>
          )}
        </div>

        {/* Link + copy */}
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={link}
            onClick={(e) => e.target.select()}
            className="flex-1 min-w-0 text-xs bg-surface border border-gray-200 rounded-lg px-3 py-2.5 text-gray-600 outline-none focus:border-gray-300"
          />
          <button
            onClick={copy}
            title={copied ? "Copied!" : "Copy link"}
            aria-label="Copy referral link"
            style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
            className="shrink-0 flex items-center gap-1.5 px-3 rounded-lg text-white text-xs font-semibold hover:opacity-90 transition active:scale-95"
          >
            {copied ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Copied
              </>
            ) : (
              <>
                <CopyIcon />
                Copy
              </>
            )}
          </button>
        </div>

        <p className="text-[11px] text-gray-500 mt-2.5 leading-relaxed">
          Earn{" "}
          <span className="font-semibold" style={{ color: primary }}>
            {points} {points === 1 ? "point" : "points"}
          </span>{" "}
          for every new sign up through your referral link.
        </p>
      </div>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

// Ordered to match the "My Points" layout: each row reads "<activity> to get  N Points".
const POINT_LABELS = {
  post: "Make a post in a community to get",
  comment: "Comment on a community post to get",
  like: "Like a community post or comment to get",
  profile_picture: "Upload Profile picture to get",
  lesson_complete: "Mark complete a lesson to get",
  lesson_discussion: "Post or comment in a lesson discussion to get",
  lesson_discussion_like: "Like a lesson discussion to get",
  // Viral Share / referral disabled — feature commented out per request.
  // viral_share: "Viral share of referral link",
};

// Per-activity glyph for the "How to earn" rows (single SVG path each).
const ACTIVITY_ICON = {
  post: "M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z",
  comment: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
  like: "M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3",
  profile_picture: "M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z",
  lesson_complete: "M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  lesson_discussion: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
  lesson_discussion_like: "M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z",
  // Viral Share / referral disabled — feature commented out per request.
  // viral_share: "M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v13",
};

// Short call-to-action labels for the "how to earn points" chips on the hero.
const EARN_SHORT = {
  post: "Post in a community",
  comment: "Comment on a community post",
  like: "Like a community post",
  profile_picture: "Upload your profile picture",
  lesson_complete: "Complete a Lesson",
  lesson_discussion: "Join a lesson discussion",
  lesson_discussion_like: "Like a lesson discussion",
  // Viral Share / referral disabled — feature commented out per request.
  // viral_share: "Refer a friend",
};

// Gold / silver / bronze medal backgrounds for the top-3 leaderboard ranks.
const RANK_MEDAL_BG = {
  1: "linear-gradient(135deg, #FDE68A 0%, #F59E0B 100%)",
  2: "linear-gradient(135deg, #F1F5F9 0%, #94A3B8 100%)",
  3: "linear-gradient(135deg, #FED7AA 0%, #EA580C 100%)",
};

/**
 * The one progress bar the gamification surfaces share — level progress in the
 * standing hero and the My Points card, and points-against-the-leader on the
 * leaderboard rows. They were three near-identical flat bars; this gives them a
 * recessed track, a gradient fill with a glass highlight and a coloured glow, so
 * the same shape reads the same everywhere.
 */
function ProgressBar({ pct, primary, height = "h-2.5", className = "" }) {
  const width = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <span
      className={`block ${height} w-full overflow-hidden rounded-full ${className}`}
      style={{ backgroundColor: `${primary}12`, boxShadow: `inset 0 1px 3px ${primary}26` }}
    >
      <span
        className="relative block h-full rounded-full transition-[width] duration-500 ease-out"
        style={{
          width: `${width}%`,
          background: `linear-gradient(90deg, ${primary} 0%, var(--brand-secondary, ${primary}bb) 100%)`,
          boxShadow: `0 1px 8px -1px ${primary}99`,
        }}
      >
        <span aria-hidden className="absolute inset-x-0 top-0 h-1/2 rounded-full bg-gradient-to-b from-surface/50 to-transparent" />
      </span>
    </span>
  );
}

// Ink for text sitting ON a medal gradient. White washes out against the pale
// top half of all three (a white "2" on silver is barely there), so each medal
// gets a deep tone from its own family instead.
const RANK_MEDAL_INK = { 1: "#78350F", 2: "rgb(var(--c-slate-700))", 3: "#7C2D12" };


// Premium LIGHT section header — subtle brand-tinted background, brand-colored
// icon chip, a title + supporting subtitle, and a soft count pill. Used by the
// Members / Rewards panels for a cleaner, more modern hierarchy.
export function SectionHeader({ iconPath, icon = null, title, subtitle, trailing, primary, action }) {
  return (
    <div
      className={`flex items-center gap-4 px-5 sm:px-6 py-4 sm:py-5 ${action ? "flex-wrap gap-y-3" : ""}`}
      style={{
        background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
        borderBottom: `1px solid ${primary}1f`,
      }}
    >
      <span
        className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border bg-surface"
        style={{ color: primary, borderColor: `${primary}29` }}
      >
        {/* `icon` wins when a section has real art of its own (badges); the
            stroked path stays the default for the rest. */}
        {icon || (
          <svg className="w-[22px] h-[22px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
          </svg>
        )}
      </span>
      <div className="min-w-0">
        <h2 className="text-xl font-bold tracking-tight text-gray-900 leading-tight">{title}</h2>
        {subtitle && <p className="text-[13px] text-gray-500 mt-0.5 truncate">{subtitle}</p>}
      </div>
      {/* With an `action`, the row wraps on mobile: icon + title + count pill stay
          on line one and the action takes line two full-width. From sm up the
          action is pushed right and the pill sits beside it. */}
      {trailing != null && (
        <span
          className={`shrink-0 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap ml-auto ${action ? "order-2 sm:order-3 sm:ml-0" : ""}`}
          style={{ backgroundColor: `${primary}14`, color: primary }}
        >
          {trailing}
        </span>
      )}
      {action && (
        <div className="order-3 w-full shrink-0 sm:order-2 sm:ml-auto sm:w-auto">{action}</div>
      )}
    </div>
  );
}

/**
 * Premium "My Points" card — gradient header with the member's identity, floating
 * rank/points stat tiles, an iconified "how to earn" list, and the referral link.
 * Shared by every page that shows points so the design stays consistent.
 */
// Friendly one-line description of the member's current level + what's next.
function levelBlurb(level, total) {
  if (!level) return "";
  if (level.is_max) return "You've reached the highest level — you're a top member of this community! 🏆";
  const n = level.level_no || 1;
  if (n === 1) return `You're just getting started — stay active to reach ${level.next_name} and unlock more.`;
  if (total && n >= total - 1) return `Almost at the top! Just ${level.points_to_next} more points to reach ${level.next_name}.`;
  return `Great progress! Keep posting and engaging to reach ${level.next_name}.`;
}

// Standalone "Levels" card — the member's level ladder. Rendered inside the
// My Points card on most pages, and on its own in the leaderboard rail (where
// the full My Points card is replaced by the "Your standing" hero).
export function LevelsCard({ me, primary }) {
  if (!me || !me.levels || me.levels.length === 0) return null;
  return (
    <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 17l6-6 4 4 7-7M21 7v5m0-5h-5" />
            </svg>
          </span>
          <h3 className="font-bold text-[15px] tracking-wide text-gray-900">Levels</h3>
        </div>
        <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
          {me.levels.filter((l) => l.achieved).length}/{me.levels.length} unlocked
        </span>
      </div>
      <ul className="space-y-2">
        {me.levels.map((lv) => {
          const done = lv.achieved;
          const cur = lv.is_current;
          return (
            <li
              key={lv.level_no}
              className="flex items-center gap-3 rounded-xl border p-2.5"
              style={{
                borderColor: cur ? primary : done ? `${primary}33` : "#f0f0f0",
                backgroundColor: cur ? `${primary}0f` : done ? `${primary}08` : "rgb(var(--c-surface-sunken))",
              }}
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-extrabold shrink-0"
                style={
                  done
                    ? { backgroundColor: primary, color: "rgb(var(--c-surface))" }
                    : { backgroundColor: "rgb(var(--c-surface))", color: "rgb(var(--c-gray-400))", border: "1px solid rgb(var(--c-gray-200))" }
                }
              >
                {done ? lv.level_no : <LockMini />}
              </span>

              <span className="flex flex-col min-w-0 flex-1 leading-tight">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className={`truncate text-sm font-bold ${done ? "text-gray-900" : "text-gray-400"}`}>{lv.name}</span>
                  {cur && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                      style={{ backgroundColor: `${primary}26`, color: primary }}
                    >
                      Current
                    </span>
                  )}
                </span>
                {done ? (
                  lv.achieved_at ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
                      <TrophyMini className="w-3 h-3" />
                      Reached {fmtAchieved(lv.achieved_at)}
                    </span>
                  ) : (
                    <span className="text-[11px] font-semibold text-gray-400">Completed</span>
                  )
                ) : (
                  <span className="text-[11px] font-medium text-gray-400">{lv.points_required} pts to unlock</span>
                )}
              </span>

              {done && (
                <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke={primary} strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function MyPointsCard({ me, pts = {}, token, groupToken, primary, member, ready = true }) {
  const earnRows = Object.keys(POINT_LABELS).filter((k) => (pts[k] || 0) > 0);
  const headerBg = `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`;
  // rank is null until the member scores — an unranked member gets no medal.
  const showMedal = !!me && me.rank != null && me.rank <= 3;
  const medalColor = me && (me.rank === 1 ? "#f59e0b" : me.rank === 2 ? "#9ca3af" : "#d97706");

  // Earned badges shown alongside points (only the ones this member has earned,
  // rendered small so the card height barely changes).
  const [earnedBadges, setEarnedBadges] = useState([]);
  const memberId = member?.community_member_id;
  useEffect(() => {
    if (!token || !memberId) {
      setEarnedBadges([]);
      return;
    }
    // Wait for the group the same way useMyPoints does. This effect shares that
    // hook's rail request, so firing before the group token resolves would send
    // one request against a null group and another against the real one — the
    // duplicate the gate exists to prevent, just on this path.
    if (!ready) return;
    let active = true;
    // Same request the points card above already makes — /membership-rail
    // carries the badges beside the standing, so this shares it rather than
    // opening a second call for the same rail. The body must match railBody()
    // exactly or cachedPost keys them apart and the sharing is lost.
    cachedPost(`${config.COMMUNITY_API_URL}/membership-rail`,
      railBody({ token, groupToken, member: { community_member_id: memberId, auth_token: member?.auth_token }, parts: RAIL_PARTS }),
      { ttl: 30000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setEarnedBadges((res.data.badges || []).filter((b) => b.earned));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, memberId, ready]);

  // Gamification is opt-in. It counts as "set up" only when the admin has given
  // at least one activity a point value > 0 (Set Activity Points). Until then
  // members can't earn anything, so the whole "My Points & Badges" card is hidden
  // on every page — no empty 0-points / "— rank" shell.
  const hasPoints = Object.values(pts).some((v) => Number(v) > 0);
  if (!hasPoints) return null;

  return (
    <div className="space-y-4">
      {/* ============ Card 1 — My Points ============ */}
      <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Light theme header — title, member, and Rank/Points at the top */}
      <div className="px-4 pt-3.5 pb-4" style={{ background: headerBg, borderBottom: `1px solid ${primary}1f` }}>
        <div className="flex items-center gap-2">
          {/* In a chip, not bare: a bare svg in a flex row has no shrink-0 and
              gets squeezed to a sliver once the title needs the space. */}
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3" />
            </svg>
          </span>
          <h3 className="font-bold text-[15px] tracking-wide text-gray-900">My Points &amp; Badges</h3>
        </div>

        {me ? (
          <>
            <div className="flex items-center gap-2.5 mt-3">
              <Avatar name={me.name} img={me.img} primary={primary} size={36} />
              <div className="min-w-0">
                <p className="font-bold truncate text-sm leading-tight text-gray-900">{me.name}</p>
                <p className="text-[11px] text-gray-500">of {me.total} members</p>
              </div>
            </div>
            {/* Rank + Points */}
            <div className="grid grid-cols-2 gap-2.5 mt-3">
              <div className="rounded-xl bg-surface border border-gray-100 shadow-sm py-2 text-center">
                <div className="text-xl font-extrabold leading-none flex items-center justify-center gap-1" style={{ color: me.rank == null ? "#9ca3af" : primary }}>
                  {showMedal && <MedalIcon className="h-[18px] w-[18px] shrink-0" style={{ color: medalColor }} />}
                  {me.rank == null ? "—" : `#${me.rank}`}
                </div>
                <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mt-1">Rank</div>
              </div>
              <div className="rounded-xl bg-surface border border-gray-100 shadow-sm py-2 text-center">
                <div className="text-xl font-extrabold leading-none" style={{ color: primary }}>{me.points}</div>
                <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400 mt-1">Points</div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500 mt-3">Sign in to see your rank and points.</p>
        )}
      </div>

      {/* Earned badges — only the badges this member has unlocked, kept small. */}
      {me && earnedBadges.length > 0 && (
        <div className="px-4 pt-3.5 pb-3.5">
          <div className="mb-2 flex items-center gap-1.5">
            <svg className="h-3.5 w-3.5" style={{ color: primary }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l2.39 4.84 5.34.78-3.86 3.77.91 5.32L12 14.98 7.22 16.5l.91-5.32L4.27 7.41l5.34-.78z" />
            </svg>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primary }}>
              Earned badges · {earnedBadges.length}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {earnedBadges.map((b) => (
              <span
                key={b.id}
                title={b.name}
                className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-surface shadow-sm ring-1 ring-gray-100"
              >
                <img src={b.image || DEFAULT_BADGE_IMAGE} alt={b.name} className="h-full w-full object-contain p-0.5" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* No level yet: the member hasn't met even the first level's requirement.
          The API now returns me.level = null for that (it used to report level 1
          regardless), so show what they need rather than a level they don't hold. */}
      {me && !me.level && me.levels?.length > 0 && (
        <div className="px-4 py-4">
          <div
            className="overflow-hidden rounded-2xl border p-4"
            style={{ borderColor: "rgb(var(--c-gray-200))", backgroundColor: "rgb(var(--c-surface-sunken))" }}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gray-200 text-gray-400">
                <LockMini className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Your level</p>
                <p className="truncate text-lg font-extrabold leading-tight text-gray-500">Not yet</p>
              </div>
            </div>
            <p className="mt-2.5 text-[12.5px] leading-relaxed text-gray-500">
              Earn {Math.max(0, (me.levels[0].points_required || 0) - (me.points || 0))} more
              {" "}pts to reach {me.levels[0].name || "Level 1"}.
            </p>
          </div>
        </div>
      )}

      {me && me.level && (
        <div className="px-4 py-4">
            <div
              className="overflow-hidden rounded-2xl border"
              style={{ borderColor: `${primary}26`, background: `linear-gradient(135deg, ${primary}12 0%, ${primary}05 100%)` }}
            >
              {/* Level header — badge + name + position */}
              <div className="flex items-center gap-3 p-4 pb-3">
                <div
                  className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-md ring-1 ring-white/30"
                  style={{ background: `linear-gradient(160deg, ${primary} 0%, var(--brand-secondary, ${primary}cc) 100%)` }}
                >
                  <span className="text-xl font-extrabold leading-none">{me.level.level_no}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: primary }}>
                    Your level{me.levels?.length ? ` · ${me.level.level_no} of ${me.levels.length}` : ""}
                  </p>
                  <p className="truncate text-lg font-extrabold leading-tight text-gray-900">{me.level.level_name}</p>
                  {me.level.achieved_at && (
                    <p className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
                      <TrophyMini className="w-3 h-3" />
                      Reached {fmtAchieved(me.level.achieved_at)}
                    </p>
                  )}
                </div>
              </div>

              {/* Short description + progress to next level */}
              <div className="px-4 pb-4">
                <p className="text-[12.5px] leading-relaxed text-gray-600">
                  {levelBlurb(me.level, me.levels?.length)}
                </p>
                {!me.level.is_max ? (
                  <>
                    <div className="mt-2.5 flex items-center justify-between text-[11px] font-bold">
                      <span className="text-gray-400">{me.points} pts</span>
                      <span style={{ color: primary }}>{me.level.points_to_next} pts to {me.level.next_name}</span>
                    </div>
                    <ProgressBar pct={me.level.progress_pct} primary={primary} className="mt-1.5" />
                  </>
                ) : (
                  <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold" style={{ backgroundColor: `${primary}1a`, color: primary }}>
                    🏆 Max level reached
                  </div>
                )}
              </div>
            </div>
        </div>
      )}
      </div>
      {/* /Card 1 — My Points */}

      {/* ============ Card 2 — Levels ============ */}
      <LevelsCard me={me} primary={primary} />
      {/* /Card 2 — Levels */}

      {/* ============ Card 3 — How to earn points ============ */}
      {me && earnRows.length > 0 && (
        <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ backgroundColor: `${primary}14`, color: primary }}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            <h3 className="font-bold text-[15px] tracking-wide text-gray-900">How to earn points</h3>
          </div>
          <ul className="divide-y divide-gray-50">
                {earnRows.map((k) => (
                  <li key={k} className="flex items-center gap-3 py-2">
                    <span
                      className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${primary}12`, color: primary }}
                    >
                      <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d={ACTIVITY_ICON[k]} />
                      </svg>
                    </span>
                    <span className="text-[13px] text-gray-600 flex-1 min-w-0">{POINT_LABELS[k]}</span>
                    <span
                      className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: `${primary}14`, color: primary }}
                    >
                      +{pts[k]} pts
                    </span>
                  </li>
                ))}
              </ul>
          {/* Viral Share / referral disabled — feature commented out per request.
          {me.url_token && (pts.viral_share || 0) > 0 && (
            <ReferralLink token={token} groupToken={groupToken} urlToken={me.url_token} primary={primary} points={pts.viral_share} />
          )} */}
        </div>
      )}
      {/* /Card 3 — How to earn points */}
    </div>
  );
}

/** Fetch the signed-in member's standing (rank/points) + point settings. */
export function useMyPoints(token, groupToken, member, withBoard = false, ready = true) {
  const [me, setMe] = useState(null);
  const [pts, setPts] = useState({});
  const memberId = member?.community_member_id;
  // Exposed so callers can re-pull the standing right after a point-earning
  // action (post, comment, …) so "My Points" updates immediately, no refresh.
  const doFetch = useCallback((force) => {
    if (!token) return Promise.resolve();
    // The group token is resolved asynchronously (it comes from the groups
    // list), so a caller inside a group would otherwise fetch once with a null
    // group and again with the real one — two requests per page, the first of
    // them answering a question nobody asked. `ready` lets the caller say the
    // group context has settled. It defaults to true so a caller with no group
    // to wait for is unaffected.
    if (!ready) return Promise.resolve();
    // The Leaderboard page needs the full board, so it keeps talking to
    // /membership-leaderboard. Everywhere else this is the rail's points card,
    // which /membership-rail answers together with the badges beside it — one
    // request instead of two, and the membership resolved once instead of twice.
    if (!withBoard) {
      return cachedPost(`${config.COMMUNITY_API_URL}/membership-rail`,
        railBody({ token, groupToken, member: { community_member_id: memberId, auth_token: member?.auth_token }, parts: RAIL_PARTS }),
        { ttl: 30000, force })
        .then((res) => {
          if (res.data?.error === 0) {
            setMe(res.data.me || null);
            setPts(res.data.points_settings || {});
          }
        })
        .catch(() => {});
    }
    return cachedPost(`${config.COMMUNITY_API_URL}/membership-leaderboard`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: memberId || undefined,
        // card_only asks the server to skip the 50-strong board and every board
        // member's level, which this card does not render — it wants `me` and
        // `points_settings` only.
        //
        // EXCEPT on the Leaderboard page, where LeaderboardView mounts beside
        // this hook and asks for the full board with this same body minus the
        // flag. Two different bodies are two different cache keys, so asking
        // for less there would cost that tab an EXTRA request rather than
        // saving one. Dropping the flag while the board is on screen keeps both
        // requests identical, so cachedPost still collapses them into one.
        //
        // Every caller of useMyPoints that is mounted at the same time has to
        // agree on this — MembershipHome stays mounted above MembershipShell,
        // so both pass it. A caller that disagreed would reintroduce the
        // second request rather than break anything.
        ...(withBoard ? {} : { card_only: 1 }),
      // 30s, not 8s. Several surfaces mount their own useMyPoints (the shell's
      // card and the active view's), and on a slow page the second mount landed
      // OUTSIDE an 8s window — so the cache missed and the identical leaderboard
      // query ran twice per page view. Staleness is not the trade-off it looks
      // like: any point-earning action calls refresh(), which forces a fresh
      // pull and notifies every other surface to do the same.
      }, { ttl: 30000, force })
      .then((res) => {
        if (res.data?.error === 0) {
          setMe(res.data.me || null);
          setPts(res.data.points_settings || {});
        }
      })
      .catch(() => {});
  }, [token, groupToken, memberId, withBoard, ready]);
  // Mount uses the coalesced cache (collapses the page-load burst + the full
  // leaderboard's identical call); the exposed refresh forces a fresh pull right
  // after a point-earning action so "My Points" still updates immediately.
  useEffect(() => {
    doFetch(false);
  }, [doFetch]);
  // Re-pull when ANY surface reports a point change, not just this one. Several
  // surfaces mount their own useMyPoints, so without this a card on one tab kept
  // showing a stale total after points were earned on another.
  const reload = useCallback(() => doFetch(true), [doFetch]);
  useEffect(() => onPointsChanged(reload), [reload]);
  const refresh = useCallback(() => {
    emitPointsChanged(reload); // update the other surfaces' cards too
    return reload();
  }, [reload]);
  return { me, pts, refresh };
}

// Celebratory "your standing" banner — the member's own rank, points, level and
// progress to the next level, front and centre so they feel their progress.
function YourStandingHero({ me, pts = {}, primary, token, groupToken, member }) {
  const lvl = me.level;
  // rank is null until the member scores — no medal, and no "#N" to announce.
  const showMedal = me.rank != null && me.rank <= 3;
  const medalColor = me.rank === 1 ? "#f59e0b" : me.rank === 2 ? "#9ca3af" : "#d97706";
  const earnRows = Object.keys(EARN_SHORT).filter((k) => (pts[k] || 0) > 0);

  // Earned badges — same source as the My Points card (only the ones unlocked).
  const [earnedBadges, setEarnedBadges] = useState([]);
  const memberId = member?.community_member_id;
  useEffect(() => {
    if (!token || !memberId) {
      setEarnedBadges([]);
      return;
    }
    let active = true;
    cachedPost(`${config.COMMUNITY_API_URL}/membership-rewards`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: memberId,
        member_token: member?.auth_token,
      }, { ttl: 30000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setEarnedBadges((res.data.badges || []).filter((b) => b.earned));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, memberId]);

  return (
    <div
      className="overflow-hidden rounded-2xl border p-5 shadow-sm sm:p-6"
      style={{
        background: `linear-gradient(135deg, ${primary}1f 0%, ${primary}0d 100%)`,
        borderColor: `${primary}26`,
      }}
    >
      <div className="flex items-center gap-4">
        <span className="shrink-0 rounded-full bg-surface p-[3px] shadow-sm ring-1" style={{ "--tw-ring-color": `${primary}33` }}>
          <Avatar name={me.name} img={me.img} primary={primary} size={56} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>Your standing</p>
          <p className="flex items-center gap-1.5 text-2xl font-extrabold leading-tight text-gray-900 sm:text-[26px]">
            {showMedal && <MedalIcon className="h-6 w-6 shrink-0" style={{ color: medalColor }} />}
            {me.rank == null ? (
              <span>Not ranked yet <span className="text-base font-semibold text-gray-400">of {me.total}</span></span>
            ) : (
              <span>You're #{me.rank} <span className="text-base font-semibold text-gray-400">of {me.total}</span></span>
            )}
          </p>
          {lvl && (
            <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-bold" style={{ backgroundColor: `${primary}14`, color: primary }}>
              <span className="flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-extrabold text-white" style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}>{lvl.level_no}</span>
              {lvl.level_name}
            </span>
          )}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-3xl font-extrabold leading-none sm:text-4xl" style={{ color: primary }}>{me.points}</p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">points</p>
        </div>
      </div>
      {lvl && (
        <p className="mt-3 text-[12.5px] leading-relaxed text-gray-600">{levelBlurb(lvl, me.levels?.length)}</p>
      )}
      {lvl && !lvl.is_max ? (
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] font-bold">
            <span className="text-gray-500">Level {lvl.level_no} · {lvl.level_name}</span>
            <span style={{ color: primary }}>{lvl.points_to_next} pts to {lvl.next_name} →</span>
          </div>
          <ProgressBar pct={lvl.progress_pct} primary={primary} className="mt-1.5" />
        </div>
      ) : lvl && lvl.is_max ? (
        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-bold" style={{ backgroundColor: `${primary}1a`, color: primary }}>
          🏆 You've reached the top level — legend status!
        </div>
      ) : null}

      {/* Earned badges — the badges this member has unlocked, kept small. */}
      {earnedBadges.length > 0 && (
        <div className="mt-4 border-t pt-3" style={{ borderColor: `${primary}1f` }}>
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>
            Earned badges · {earnedBadges.length}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {earnedBadges.map((b) => (
              <span
                key={b.id}
                title={b.name}
                className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-surface shadow-sm ring-1 ring-gray-100"
              >
                <img src={b.image || DEFAULT_BADGE_IMAGE} alt={b.name} className="h-full w-full object-contain p-0.5" />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* What to do next to earn points */}
      {earnRows.length > 0 && (
        <div className="mt-4 border-t pt-3" style={{ borderColor: `${primary}1f` }}>
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: primary }}>
            {lvl && !lvl.is_max
              ? `${lvl.points_to_next} pts to ${lvl.next_name} — here's how to earn them`
              : "Keep earning points — here's how"}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {earnRows.map((k) => (
              <span
                key={k}
                className="inline-flex items-center gap-1.5 rounded-full bg-surface px-2.5 py-1 text-[12px] font-semibold text-gray-600 shadow-sm ring-1 ring-gray-100"
              >
                <svg className="h-3.5 w-3.5" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d={ACTIVITY_ICON[k]} />
                </svg>
                {EARN_SHORT[k]}
                <b className="font-extrabold" style={{ color: primary }}>+{pts[k]}</b>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Top-3 podium — a small stage: gold centre (raised), silver left, bronze right.
// The three pedestals share one baseline and the stage closes with a floor rule,
// so they read as standing on something instead of being cut off by the list.
function Podium({ top3, primary, meId }) {
  const slots = [top3[1], top3[0], top3[2]].filter(Boolean); // 2nd · 1st · 3rd
  const meta = {
    1: { ped: "h-[118px] sm:h-[142px]", av: 78, glow: "#F59E0B" },
    2: { ped: "h-[80px] sm:h-[96px]", av: 60, glow: "#94A3B8" },
    3: { ped: "h-[62px] sm:h-[74px]", av: 60, glow: "#EA580C" },
  };
  return (
    <div
      className="relative overflow-hidden border-b border-gray-100 px-3 pt-7 sm:px-6"
      // The stage is tinted rather than plain white, so the podium reads as its
      // own space instead of three avatars floating in the card.
      style={{ background: `linear-gradient(180deg, ${primary}12 0%, ${primary}05 46%, rgb(var(--c-surface)) 100%)` }}
    >
      {/* Stage light — a soft brand glow falling on the winner. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-56"
        style={{ background: `radial-gradient(52% 74% at 50% 0%, ${primary}24 0%, transparent 72%)` }}
      />
      <div className="relative grid grid-cols-3 items-end gap-2 sm:gap-5">
        {slots.map((m) => {
          const cfg = meta[m.rank];
          const ink = RANK_MEDAL_INK[m.rank];
          const isMe = !!meId && m.community_member_id === meId;
          return (
            <div key={m.rank} className="flex min-w-0 flex-col items-center">
              {m.rank === 1 && (
                <svg className="mb-1.5 h-7 w-7 text-amber-400 drop-shadow-sm" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 7l4.5 4L12 5l4.5 6L21 7l-1.6 9.3a1 1 0 01-1 .85H5.6a1 1 0 01-1-.85L3 7z" />
                </svg>
              )}
              <span
                className="rounded-full p-[3px]"
                style={{ background: RANK_MEDAL_BG[m.rank], boxShadow: `0 14px 30px -12px ${cfg.glow}` }}
              >
                <span className="block rounded-full ring-[3px] ring-surface">
                  <Avatar name={m.name} img={m.img} primary={primary} size={cfg.av} />
                </span>
              </span>
              <p className="mt-2.5 flex max-w-full items-center justify-center gap-1 text-[13.5px] font-bold leading-tight text-gray-900">
                <span className="truncate">{m.name}</span>
                {isMe && (
                  <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-wide" style={{ color: primary }}>You</span>
                )}
              </p>
              {m.level && (
                <p className="mt-0.5 max-w-full truncate text-[11px] font-semibold text-gray-400">{m.level.level_name}</p>
              )}
              {/* Pedestal — bottom-aligned so all three stand on the floor rule.
                  The score lives on the block it earned, which is what makes the
                  pedestal worth its height instead of being a coloured stub. */}
              <div
                className={`relative mt-2.5 flex w-full ${cfg.ped} flex-col items-center gap-1.5 overflow-hidden rounded-t-2xl pt-2.5`}
                style={{
                  background: RANK_MEDAL_BG[m.rank],
                  boxShadow: "inset 0 -18px 26px -20px rgba(0,0,0,.5)",
                }}
              >
                <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/45 to-transparent" />
                <span className="relative text-2xl font-extrabold leading-none sm:text-[28px]" style={{ color: ink }}>{m.rank}</span>
                <span className="relative inline-flex items-baseline gap-1 rounded-full bg-surface/75 px-2 py-0.5 shadow-sm">
                  <span className="text-[12px] font-extrabold leading-none tabular-nums" style={{ color: ink }}>{m.points}</span>
                  <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: ink, opacity: 0.7 }}>pts</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LeaderboardView({ token, groupToken, primary, member, groupCard, topBanner }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  const [pts, setPts] = useState({});

  useEffect(() => {
    let active = true;
    setLoading(true);
    cachedPost(`${config.COMMUNITY_API_URL}/membership-leaderboard`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
      }, { ttl: 8000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setRows(res.data.leaderboard || []);
        setMe(res.data.me || null);
        setPts(res.data.points_settings || {});
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken, member?.community_member_id]);

  // Top 3 get a podium; ranks 4–15 render as list rows.
  const podium = rows.length >= 3 ? rows.slice(0, 3) : [];
  const listRows = rows.length >= 3 ? rows.slice(3, 15) : rows.slice(0, 15);
  // Every row's score as a share of the leader's, drawn as a bar. It turns a
  // column of bare numbers into a shape you can read at a glance — and gives the
  // rows something to fill the width with when a member has no level yet.
  const topPoints = rows[0]?.points || 0;

  return (
    <div className="tpl-rail-grid">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className=" space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
        {groupCard}
        {/* My Points lives in the "Your standing" hero at the top, so it's not
            repeated here — but the Levels ladder still belongs in the rail. */}
        <LevelsCard me={me} primary={primary} />
        <InlineBanner token={token} groupToken={groupToken} placement="leaderboard" wrapperClass="space-y-3" />
      </aside>

      {/* Your standing + leaderboard — main column on the left on desktop */}
      <div className=" order-1 space-y-5">
        {topBanner}
        {!loading && me && <YourStandingHero me={me} pts={pts} primary={primary} token={token} groupToken={groupToken} member={member} />}

        <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <SectionHeader
            primary={primary}
            iconPath="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4zM7 5H4v2a3 3 0 003 3M17 5h3v2a3 3 0 01-3 3"
            title="Leaderboard"
            subtitle="Top members ranked by points"
            trailing={rows.length > 0 ? `Top ${Math.min(rows.length, 15)}` : null}
          />
          {loading ? (
            <LeaderboardSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState art="leaderboard" accent={primary} title="The board is still empty" message="Points are awarded for posting, commenting, finishing lessons and joining events. As soon as members start earning, the top of the table fills in here." />
          ) : (
            <>
              {podium.length === 3 && <Podium top3={podium} primary={primary} meId={member?.community_member_id} />}
              {listRows.length > 0 && (
              /* Ranks below the podium: one clean divided list rather than a stack
                 of pale gradient pills, which washed the names out and made every
                 row compete with the podium above it. */
              <ul className="divide-y divide-gray-100">
              {listRows.map((m) => {
                const top = m.rank <= 3;
                // By identity, not rank: an unscored member has no rank of their own.
                const isMe = !!member?.community_member_id && m.community_member_id === member.community_member_id;
                return (
                  <li
                    key={m.rank}
                    className="group relative flex min-h-[72px] items-center gap-3 px-4 py-3.5 transition-colors hover:bg-gray-50/80 sm:gap-4 sm:px-5"
                    // Your own row is marked by a brand tint and a spine down the
                    // left edge — findable at a glance without shouting.
                    style={isMe ? { backgroundColor: `${primary}0a`, boxShadow: `inset 3px 0 0 0 ${primary}` } : undefined}
                  >
                    {/* Rank — medal chip for the top three, a quiet numbered chip
                        after. Both are the same size, so the column stays a column
                        instead of jumping between a badge and bare text. */}
                    {top ? (
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold shadow-sm ring-1 ring-surface/70"
                        style={{ background: RANK_MEDAL_BG[m.rank], color: RANK_MEDAL_INK[m.rank] }}
                      >
                        {m.rank}
                      </span>
                    ) : (
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold tabular-nums ring-1"
                        style={
                          isMe
                            ? { backgroundColor: `${primary}1f`, color: primary, boxShadow: `inset 0 0 0 1px ${primary}40` }
                            : { backgroundColor: "rgb(var(--c-surface-sunken))", color: "rgb(var(--c-slate-400))", boxShadow: "inset 0 0 0 1px #F1F5F9" }
                        }
                      >
                        {m.rank}
                      </span>
                    )}

                    <span className="shrink-0 rounded-full shadow-[0_2px_10px_rgba(15,23,42,0.12)] ring-2 ring-surface">
                      <Avatar name={m.name} img={m.img} primary={primary} size={44} />
                    </span>

                    {/* Name + level achievement */}
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-[15px] font-bold tracking-tight text-gray-900">{m.name}</span>
                        {isMe && (
                          <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                            style={{ backgroundColor: `${primary}1f`, color: primary }}
                          >
                            You
                          </span>
                        )}
                      </span>

                      {m.level && (
                        <span className="flex min-w-0 items-center gap-2 text-[11px] leading-none">
                          <span
                            className="inline-flex shrink-0 items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 font-bold"
                            style={{ backgroundColor: `${primary}14`, color: primary }}
                            title={`Level ${m.level.level_no}`}
                          >
                            <span
                              className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-extrabold"
                              style={{ backgroundColor: primary, color: "var(--brand-btn-text, #fff)" }}
                            >
                              {m.level.level_no}
                            </span>
                            <span className="truncate">{m.level.level_name}</span>
                          </span>
                          {m.level.achieved_at && (
                            <span className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-gray-400 sm:inline-flex">
                              <TrophyMini className="h-3 w-3" />
                              <span className="font-semibold">{fmtAchieved(m.level.achieved_at)}</span>
                            </span>
                          )}
                        </span>
                      )}

                      {/* Score bar — this member's points against the leader's.
                          Floored at 6% so the smallest score is still a visible
                          sliver rather than an empty track. */}
                      {topPoints > 0 && (
                        <span className="mt-0.5 block w-full max-w-[320px]">
                          <ProgressBar
                            pct={Math.max(6, Math.round((m.points / topPoints) * 100))}
                            primary={primary}
                            height="h-2"
                          />
                        </span>
                      )}
                    </span>

                    {/* Points — a tinted pill, so the score reads as a value the
                        row carries rather than loose text drifting at the edge. */}
                    <span
                      className="inline-flex shrink-0 items-baseline gap-1 rounded-xl px-3 py-1.5"
                      style={{ backgroundColor: `${primary}0f`, boxShadow: `inset 0 0 0 1px ${primary}1f` }}
                    >
                      <span className="text-[17px] font-extrabold leading-none tabular-nums" style={{ color: primary }}>{m.points}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: primary, opacity: 0.65 }}>pts</span>
                    </span>
                  </li>
                );
              })}
            </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EventCard({ e, primary, past, live, tz }) {
  const online = (e.event_type || 1) <= 3;
  const lo = toZonedEvent(e, tz);
  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-surface shadow-sm transition hover:shadow-md sm:flex-row sm:items-stretch ${past ? "opacity-75" : ""}`}>
      {/* Image — full-width on mobile, fixed side column on sm+ */}
      <div
        className="relative h-40 w-full shrink-0 sm:h-auto sm:w-44 md:w-48"
        style={{ background: `linear-gradient(135deg, ${primary}29 0%, ${primary}0d 100%)` }}
      >
        <img
          src={eventImage(e)}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(ev) => {
            ev.currentTarget.style.display = "none";
          }}
        />
      </div>

      {/* Content + CTA — stacks under the image on mobile, sits inline on sm+ */}
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: `${primary}14`, color: primary }}
            >
              {online ? "Online" : "In person"}
            </span>
            {live && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" style={{ background: primary }}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-surface/80" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-surface" />
                </span>
                Live now
              </span>
            )}
            {past && <span className="text-[11px] font-medium text-gray-400">Past</span>}
          </div>
          <h3 className="truncate text-[15px] font-bold text-gray-900">{e.title}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
              </svg>
              {lo.dateLabel}{lo.timeLabel ? ` · ${lo.timeLabel}` : ""}
            </span>
            {e.location && (
              <span className="flex min-w-0 items-center gap-1.5">
                <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="truncate">{e.location}</span>
              </span>
            )}
          </div>
          {e.description && <p className="mt-1.5 line-clamp-2 text-sm text-gray-500">{e.description}</p>}
        </div>

        {/* CTA — full-width button on mobile, inline on sm+ */}
        {e.link && !past ? (
          <a
            href={e.link}
            target="_blank"
            rel="noreferrer"
            style={{ color: primary, borderColor: primary }}
            className="block w-full shrink-0 whitespace-nowrap rounded-lg border px-4 py-2 text-center text-sm font-bold uppercase tracking-wide transition hover:bg-gray-50 active:scale-[0.98] sm:w-auto"
          >
            {e.cta || (online ? "Join" : "View")}
          </a>
        ) : past ? (
          <span className="block w-full shrink-0 whitespace-nowrap rounded-lg border border-gray-200 px-4 py-2 text-center text-sm font-semibold text-gray-400 sm:w-auto">
            Ended
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* Compact event pill inside a month-grid day cell. Clicking it opens the event
   detail modal without also toggling the day cell it sits in (stopPropagation).
   Past events drop to a grey scheme so the month reads at a glance. */
function CalEventChip({ e, tz, primary, onOpen }) {
  const lo = toZonedEvent(e, tz);
  const isPast = !e.upcoming;
  return (
    <button
      onClick={(ev) => {
        ev.stopPropagation();
        onOpen(e);
      }}
      title={`${lo.timeLabel ? fmtTime(lo.timeLabel) + " · " : ""}${e.title}`}
      className="flex w-full items-center gap-1 overflow-hidden rounded-md py-[3px] pl-1.5 pr-1 text-left text-[11px] font-semibold leading-tight transition hover:-translate-y-px hover:shadow-sm"
      style={
        isPast
          ? { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-400))", boxShadow: "inset 2px 0 0 #d1d5db" }
          : { backgroundColor: `${primary}14`, color: primary, boxShadow: `inset 2px 0 0 ${primary}` }
      }
    >
      {lo.timeLabel && <span className="shrink-0 tabular-nums opacity-80">{fmtTime(lo.timeLabel)}</span>}
      <span className="truncate">{e.title}</span>
    </button>
  );
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const localIso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtTime = (t) => (t ? t.toLowerCase().replace(/\s+/g, "") : "");


// The instant at which the VIEWER's own clock reads the same wall time this
// event shows in `tz`. Powers the countdown — see the startMs note below.
const wallClockMs = (d, tz) => {
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        ...(tz ? { timeZone: tz } : {}),
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
        .formatToParts(d)
        .map((x) => [x.type, x.value])
    );
    // hour12:false reports midnight as "24" in some engines.
    const hour = Number(p.hour) % 24;
    return new Date(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), 0, 0).getTime();
  } catch {
    return d.getTime();
  }
};

/**
 * The instant a wall clock refers to WITHIN a named zone.
 *
 * Distinct from wallClockMs() above, which answers the opposite question ("when
 * does the VIEWER's clock read this?"). Event times are stored as the bare wall
 * clock the creator typed, so they belong to the creator's account timezone —
 * turning that into a real instant is what lets every member, wherever they are,
 * agree on when the event starts.
 *
 * Two passes: guess the instant as if the wall clock were UTC, measure how far
 * that lands from the target zone, then subtract the difference. Accurate except
 * inside a DST spring-forward gap, where the wall clock names no real instant.
 */
const zonedWallClockMs = (y, mo, d, hh, mm, tz) => {
  const guess = Date.UTC(y, mo - 1, d, hh, mm, 0, 0);
  if (!tz) return guess;
  try {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      })
        .formatToParts(new Date(guess))
        .map((x) => [x.type, x.value])
    );
    const seen = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour) % 24, Number(p.minute), Number(p.second)
    );
    return guess - (seen - guess);
  } catch {
    return guess;
  }
};

/** "8:45 PM" from 24h parts — the creator's own clock, shown as the subtitle. */
const wallClockLabel = (hh, mm) => {
  const ap = hh >= 12 ? "PM" : "AM";
  const h = hh % 12 || 12;
  return `${h}:${String(mm).padStart(2, "0")} ${ap}`;
};

// The instant an event finishes, on the same wall-clock basis as startMs above.
// `end_iso` / `end_time` come from the API, which substitutes the end of the
// start day for events saved before end times existed. Returns null only when
// the event has no usable date at all.
const endWallClockMs = (e, tz) => {
  if (!e || !e.end_iso) return null;
  let hh = 23;
  let mm = 59;
  const m = String(e.end_time || "").match(/(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
  if (m) {
    hh = parseInt(m[1], 10);
    mm = parseInt(m[2], 10);
    const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && hh < 12) hh += 12;
    if (ap === "AM" && hh === 12) hh = 0;
  }
  const [y, mo, dd] = String(e.end_iso).split("-").map(Number);
  if (!y || !mo || !dd) return null;
  // Same basis as startMs: the creator's wall clock, resolved in their zone.
  return zonedWallClockMs(y, mo, dd, hh, mm, tz);
};

// Event times are stored in UTC. The API sends the UTC date (`date_iso`, Y-m-d)
// and UTC time (`time`, "h:mm AM/PM") plus the community `timezone`; we rebuild
// that exact instant and render it in the COMMUNITY timezone (same for every
// viewer). Pure frontend — Intl does the conversion, no server tz math.
export function toZonedEvent(e, tz) {
  if (!e || !e.date_iso) {
    return { localIso: e?.date_iso || null, dateLabel: e?.date, timeLabel: e?.time || null, hasTime: !!e?.time, startMs: null, srcZone: tz || "", srcTimeLabel: null };
  }
  let hh = 0;
  let mm = 0;
  let hasTime = false;
  if (e.time) {
    const m = String(e.time).match(/(\d{1,2}):(\d{2})\s*([AaPp][Mm])?/);
    if (m) {
      hh = parseInt(m[1], 10);
      mm = parseInt(m[2], 10);
      const ap = (m[3] || "").toUpperCase();
      if (ap === "PM" && hh < 12) hh += 12;
      if (ap === "AM" && hh === 12) hh = 0;
      hasTime = true;
    }
  }
  const [y, mo, dd] = String(e.date_iso).split("-").map(Number);
  if (!y || !mo || !dd) {
    return { localIso: e.date_iso, dateLabel: e.date, timeLabel: e.time || null, hasTime: !!e.time, startMs: null, srcZone: tz || "", srcTimeLabel: null };
  }

  /*
   * `tz` is the SOURCE zone — the creator's account timezone (users.timezone),
   * which is the clock the stored wall time was typed on. It is NOT the zone we
   * render in.
   *
   * This used to parse the wall clock as UTC and format it back in `tz`, which
   * was only ever right because the API hardcoded `tz` to UTC: the digits came
   * out unchanged and were labelled "UTC time" — a label that was simply untrue.
   * Worse, the countdown compared that wall clock against the VIEWER's clock, so
   * an event went "LIVE NOW" at a different moment in every country.
   *
   * Now: rebuild the real instant from the creator's zone, then render it in
   * whatever zone the reader is actually in. One event, one moment, and each
   * member sees it on their own clock. `srcTimeLabel` keeps the creator's
   * original wall time for the subtitle.
   */
  const startMs = hasTime ? zonedWallClockMs(y, mo, dd, hh, mm, tz) : null;
  // An all-day event names a date, not an instant, so it must not be shifted by
  // any offset — keep it on its own date.
  const shown = hasTime && startMs != null ? new Date(startMs) : new Date(y, mo - 1, dd);

  return {
    localIso: localIso(shown),
    dateLabel: shown.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
    timeLabel: hasTime ? shown.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null,
    hasTime,
    startMs,
    endMs: endWallClockMs(e, tz),
    // The creator's own clock, for the "8:45 PM in Accra" subtitle.
    srcZone: tz || "",
    srcTimeLabel: hasTime ? wallClockLabel(hh, mm) : null,
  };
}

/**
 * Where an event sits on the clock right now: "live" | "upcoming" | "ended".
 *
 * The API only splits events into upcoming/past, so a session that has STARTED
 * but not finished still arrives flagged `upcoming` — which is why a banner
 * could read "UPCOMING" next to its own "LIVE NOW" button. Phase is derived
 * here from the same wall-clock instants the cards display (see toZonedEvent),
 * so a badge can never disagree with the time printed beside it.
 *
 * Events with a date but no time have no startMs; they stay "upcoming" until
 * their end-of-day endMs passes, which is the same rule the API applies.
 */
function eventPhase(lo, now = Date.now()) {
  if (!lo) return "upcoming";
  if (lo.endMs != null && now > lo.endMs) return "ended";
  if (lo.startMs != null && now >= lo.startMs) return "live";
  return "upcoming";
}

/** Google Maps link for a venue string, so an address is one tap from directions. */
function mapsUrl(location) {
  const q = String(location || "").trim();
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : "";
}

// A single circular-progress ring with the value + label centred (reference
// design: light grey track + a coloured arc for the unit's progress).
function CountdownRing({ value, label, max, color, size = 62 }) {
  const stroke = size <= 46 ? 3 : 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, max ? value / max : 0));
  const offset = circ * (1 - frac);
  // Scale the inner text to the ring so it stays balanced and centred at any size.
  const numFs = Math.round(size * 0.3); // 42 → 13px, 62 → 19px
  const labFs = Math.max(6, Math.round(size * 0.16)); // 42 → 7px, 62 → 10px
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-extrabold leading-none text-gray-800 tabular-nums"
          style={{ fontSize: numFs }}
        >
          {String(value).padStart(2, "0")}
        </span>
        <span
          className="mt-px font-semibold leading-none tracking-tight text-gray-500"
          style={{ fontSize: labFs }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

// Premium event countdown — four circular progress rings (Days/Hours/Minutes/
// Seconds) that tick every second. Renders a "Live now" pill once started.
function EventCountdown({ startMs, endMs = null, accent = "#F59E0B", size = 62, inline = false }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!startMs) return null;

  const diff = startMs - now;

  // Compact one-line variant for chips (e.g. "Starts in 9d 12h 30m").
  if (inline) {
    // Past its end time it is over, not live. `diff <= 0` alone treated every
    // finished event as running forever — a talk from last month still said
    // LIVE NOW with an active Join button.
    if (endMs != null && now > endMs) {
      return <span className="font-bold uppercase tracking-wide">Ended</span>;
    }
    if (diff <= 0) return <span className="font-bold uppercase tracking-wide">Live now</span>;
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const parts = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
    return <span className="font-bold">Starts in {parts}</span>;
  }

  // Finished: a flat grey pill with no pulsing dot, so it reads as over at a
  // glance rather than as a quieter shade of live.
  if (endMs != null && now > endMs) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-600">
        <span className="inline-flex h-2 w-2 rounded-full bg-gray-400" />
        Ended
      </span>
    );
  }

  if (diff <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow"
        style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}cc 100%)` }}
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-surface/70" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-surface" />
        </span>
        Live now
      </span>
    );
  }

  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const rings = [
    { value: days, label: "Days", max: 365, color: "#2EA8E6" },
    { value: hrs, label: "Hours", max: 24, color: "#FF5C4D" },
    { value: mins, label: "Minutes", max: 60, color: "#7E57C2" },
    { value: secs, label: "Seconds", max: 60, color: "#2EA8E6" },
  ];

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3">
      {rings.map((r) => (
        <CountdownRing key={r.label} value={r.value} label={r.label} max={r.max} color={r.color} size={size} />
      ))}
    </div>
  );
}

/** Skool-style event detail popup — opens when a calendar entry is clicked. */
function EventDetailModal({ e, primary, tz, onClose }) {
  if (!e) return null;
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const lo = toZonedEvent(e, tz);
  // The creator's zone, shown only when it differs from the reader's — telling
  // someone in Accra that an event is "8:45 PM in Accra" is noise.
  const srcCity = (lo.srcZone || "").split("/").pop().replace(/_/g, " ");
  const viewerCity = viewerZone.split("/").pop().replace(/_/g, " ");
  const showSrc = !!(lo.srcZone && lo.srcTimeLabel && lo.srcZone !== viewerZone);
  // Viewer-local, to match toZonedEvent().localIso — "Today" has to mean today
  // on the reader's calendar, not the creator's.
  const todayZ = localIso(new Date());
  const isToday = lo.localIso && lo.localIso === todayZ;
  const dayLabel = isToday ? "Today" : lo.dateLabel;
  const online = (e.event_type || 1) <= 3;
  // Whether the event is already over. Decided here from the same wall clock the
  // popup DISPLAYS, so the badge and the button can never disagree with the time
  // printed right above them.
  const ended = lo.endMs != null && Date.now() > lo.endMs;

  return (
    // No backdrop-click close — a stray click must not dismiss the event before
    // the member has taken in the time, link or JOIN button.
    <div className="evt-backdrop fixed inset-0 z-50 flex items-end justify-center bg-ink-slate/55 backdrop-blur-[2px] p-0 sm:items-center sm:p-4">
      <div
        className="evt-sheet flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-[1.75rem] bg-surface shadow-[0_-12px_48px_-12px_rgba(15,23,42,0.35)] sm:max-h-[90vh] sm:max-w-md sm:rounded-[1.75rem] sm:shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Header — event cover with a legibility scrim, type badge + close */}
        <div
          className="relative h-44 shrink-0 sm:h-48"
          style={{ background: `linear-gradient(135deg, ${primary}29 0%, ${primary}0d 100%)` }}
        >
          <img
            src={eventImage(e)}
            alt=""
            className="h-full w-full object-cover"
            onError={(ev) => { ev.currentTarget.style.display = "none"; }}
          />
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

          {/* iOS grab handle — phones only */}
          <span className="absolute left-1/2 top-2.5 h-1.5 w-10 -translate-x-1/2 rounded-full bg-surface/70 sm:hidden" />

          {/* Event-type badge */}
          <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-surface/90 px-2.5 py-1 text-[11px] font-bold text-gray-800 shadow-sm backdrop-blur">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9" style={{ color: primary }}>
              <path strokeLinecap="round" strokeLinejoin="round" d={online ? "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" : "M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z"} />
            </svg>
            {eventTypeLabel(e.event_type)}
          </span>

          <button
            onClick={onClose}
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-surface/85 text-gray-600 shadow-sm backdrop-blur transition hover:bg-surface active:scale-95"
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="h-4 w-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Title overlaid on the cover */}
          <div className="absolute inset-x-0 bottom-0 px-5 pb-3.5">
            <h3 className="text-[21px] font-extrabold leading-tight text-white drop-shadow-sm">{e.title}</h3>
          </div>
        </div>

        <div className="thin-scroll min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {/* Live countdown chip for upcoming events */}
          {lo.startMs && (
            <div
              className={`mb-4 inline-flex items-center gap-2 rounded-2xl px-3.5 py-2 text-[13px] font-bold ${ended ? "bg-gray-100 text-gray-500" : ""}`}
              style={ended ? undefined : { backgroundColor: `${primary}14`, color: primary }}
            >
              {ended ? (
                <span className="inline-flex h-2 w-2 rounded-full bg-gray-400" />
              ) : (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: primary }} />
                  <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: primary }} />
                </span>
              )}
              <EventCountdown startMs={lo.startMs} endMs={lo.endMs} accent={primary} inline />
            </div>
          )}

          {/* Info list — icon chip on the left, value on the right */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-[14px] font-bold text-gray-900">
                  {dayLabel}{lo.timeLabel ? ` · ${fmtTime(lo.timeLabel)}` : ""}
                </p>
                {/* The headline above is the reader's own clock. This says so,
                    and carries the organiser's time when the two differ — the
                    number that matters for an in-person venue. */}
                <p className="text-[12px] text-gray-500">
                  {showSrc
                    ? `Your local time · ${lo.srcTimeLabel} in ${srcCity}`
                    : viewerCity
                      ? `${viewerCity} time`
                      : "Your local time"}
                </p>
              </div>
            </div>

            {e.location && (
              /* The venue is the whole instruction for an in-person event, so it
                 wraps to as many lines as it needs — truncating it to
                 "Brigade World Trade Center, Chennai, Tamil Na…" hid the part a
                 member actually travels to. Tapping it opens directions. */
              <a
                href={mapsUrl(e.location)}
                target="_blank"
                rel="noreferrer"
                className="group flex items-start gap-3 rounded-2xl bg-gray-50 px-3 py-2.5 transition hover:bg-gray-100"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-[14px] font-semibold leading-snug text-gray-800">{e.location}</span>
                  <span className="mt-0.5 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: primary }}>
                    Get directions
                    <svg className="h-3 w-3 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </span>
              </a>
            )}

            {e.link && (
              <a
                href={e.link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2.5 transition hover:bg-gray-100"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  <svg className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
                  </svg>
                </span>
                <span className="min-w-0 truncate text-[13.5px] font-semibold" style={{ color: primary }}>{e.link}</span>
              </a>
            )}
          </div>

          {e.description && (
            <p className="mt-4 whitespace-pre-line text-[13.5px] leading-relaxed text-gray-600">{e.description}</p>
          )}
        </div>

        {/* Pinned footer — CTA stays visible; respects the phone's safe area. */}
        {e.link && (
          <div className="shrink-0 border-t border-gray-100 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {ended ? (
              // A real <button disabled>, not a greyed link: the meeting room is
              // closed, so the click must not go anywhere at all.
              <button
                type="button"
                disabled
                className="flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-2xl bg-gray-100 py-3.5 text-[14px] font-bold uppercase tracking-wide text-gray-400"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Event ended
              </button>
            ) : (
              <a
                href={e.link}
                target="_blank"
                rel="noreferrer"
                style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}d9 100%)`, boxShadow: `0 10px 24px -10px ${primary}`, color: "var(--brand-btn-text, rgb(var(--c-surface)))" }}
                className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-[14px] font-bold uppercase tracking-wide text-white transition hover:brightness-105 active:scale-[0.99]"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
                </svg>
                {e.cta || (online ? "Join now" : "View event")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Premium "next upcoming event" banner shown at the top of the feed, just below
 * the group hero. Renders only the single next event; clicking it (anywhere but
 * the CTA link) opens the full event detail popup. Nothing renders when there
 * is no upcoming event.
 */
export function UpcomingEventBanner({ token, groupToken, placement, coursePageId, primary, className = "" }) {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [tz, setTz] = useState("");
  const [modalOpen, setModalOpen] = useState(false); // card → detail popup

  useEffect(() => {
    let active = true;
    // Context tells the API which targeted events belong here: a group's feed
    // sends group_token; a membership page sends its placement key; a course
    // sends course_page_id. Membership-wide events come back in every context.
    const body = { community_token: token };
    if (groupToken) body.group_token = groupToken;
    if (placement) body.placement = placement;
    if (coursePageId) body.course_page_id = coursePageId;
    cachedPost(`${config.COMMUNITY_API_URL}/membership-events`, body, { ttl: 60000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setTz(res.data.timezone || "");
        // API returns upcoming events ordered by date, already filtered to this
        // context (placement / group / course page). Keep them all and rotate
        // through them one at a time in the carousel below.
        setEvents(res.data.upcoming || []);
        setIdx(0);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [token, groupToken, placement, coursePageId]);

  // Re-render on a timer so an event that ends while the page is open removes
  // itself, instead of lingering until the next navigation.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Drop anything that has already finished. The server also classifies events,
  // but it compares a UTC "now" against a wall clock that carries no timezone
  // (see toZonedEvent), so it can be a whole UTC offset out — and even a correct
  // server answer would be up to a poll stale. Deciding here, on the same wall
  // clock the banner DISPLAYS, keeps the banner honest and lets it vanish the
  // moment the event is over.
  const liveEvents = events.filter((e) => {
    const end = toZonedEvent(e, tz).endMs;
    return end == null || end > nowMs;
  });

  // Auto-rotate the banner through every upcoming event, one by one. Pauses
  // while the reader has the detail modal open.
  useEffect(() => {
    if (liveEvents.length <= 1 || modalOpen) return undefined;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % liveEvents.length);
    }, 5000);
    return () => clearInterval(id);
  }, [liveEvents.length, modalOpen]);

  const event = liveEvents.length
    ? liveEvents[Math.min(idx, liveEvents.length - 1)]
    : null;
  if (!event) return null;

  const lo = toZonedEvent(event, tz);
  // The API flags a started-but-unfinished event as `upcoming`, so the pill used
  // to read "UPCOMING" right next to this banner's own "LIVE NOW" button. Both
  // now come from the same phase.
  const phase = eventPhase(lo, nowMs);
  const phaseLabel = phase === "live" ? "Live now" : phase === "ended" ? "Ended" : "Upcoming";
  const online = (event.event_type || 1) <= 3;
  // Event banner accent follows the community's theme colour, falling back to
  // amber when no theme colour is configured.
  const accent = primary || "#F59E0B";

  // Big day number + month for the left-hand date chip.
  let day = "";
  let mon = "";
  if (lo.localIso) {
    const parts = lo.localIso.split("-");
    day = String(parseInt(parts[2], 10));
    mon = new Date(`${lo.localIso}T00:00:00`)
      .toLocaleDateString("en-US", { month: "short" })
      .toUpperCase();
  }


  return (
    <>
      {/* ═══ Desktop / tablet (lg+): original inline alert banner at the top ═══ */}
      <div
        onClick={() => setModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setModalOpen(true)}
        className={`group relative hidden cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg md:block ${className}`}
        style={{ borderColor: `${accent}33`, background: `linear-gradient(115deg, ${accent}1f 0%, rgb(var(--c-surface)) 52%)` }}
      >
        {/* Alert accent rail */}
        <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: accent }} />
        {/* Soft decorative glow */}
        <span className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
        <div className={`relative flex items-center gap-3 py-2 ${liveEvents.length > 1 ? "pl-10 pr-10 pb-5" : "px-4 pl-5"}`}>
          {/* Date chip */}
          <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg text-center text-white shadow ring-1 ring-white/30" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}d9 100%)` }}>
            <span className="text-base font-extrabold leading-none">{day || "—"}</span>
            {mon && <span className="text-[7px] font-bold tracking-[0.1em] opacity-90">{mon}</span>}
          </div>

          {/* Details — compact two-line layout */}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h3 className="min-w-0 flex-1 truncate text-sm font-bold text-gray-900">{event.title}</h3>
              {online && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-50 dark:bg-green-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-green-600 dark:text-green-300">
                  <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
                  </svg>
                  Online
                </span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0 text-[11px] font-medium text-gray-500">
              <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wide" style={{ color: accent }}>
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: accent }} />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                </span>
                {phaseLabel}
              </span>
              <span className="flex items-center gap-1">
                <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
                {lo.dateLabel}{lo.timeLabel ? ` · ${lo.timeLabel}` : ""}
              </span>
              {event.location && (
                <span className="flex items-center gap-1">
                  <svg className="h-3.5 w-3.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{event.location}</span>
                </span>
              )}
            </div>
          </div>

          {/* Inline countdown */}
          {lo.startMs && (
            <div className="hidden shrink-0 items-center md:flex">
              <EventCountdown startMs={lo.startMs} endMs={lo.endMs} accent={accent} size={42} />
            </div>
          )}

          {/* CTA */}
          <div className="hidden shrink-0 items-center sm:flex">
            {event.link ? (
              <a
                href={event.link}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundColor: accent, boxShadow: `0 6px 14px -8px ${accent}99` }}
                className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white transition hover:-translate-y-0.5 hover:opacity-95 active:scale-[0.98]"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d={online ? "M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" : "M9 5l7 7-7 7"} />
                </svg>
                {event.cta || (online ? "Join" : "View")}
              </a>
            ) : (
              <span className="flex items-center gap-1 rounded-lg border px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide transition group-hover:bg-gray-50" style={{ color: accent, borderColor: `${accent}40` }}>
                Details
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            )}
          </div>
        </div>
        {/* Carousel controls — side arrows vertically centered, dots at the bottom. */}
        {liveEvents.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous event"
              onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + liveEvents.length) % liveEvents.length); }}
              className="absolute left-1.5 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-surface/90 shadow-sm ring-1 ring-black/5 backdrop-blur transition hover:scale-105 hover:shadow active:scale-95"
              style={{ color: accent }}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="absolute bottom-1 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5">
              {liveEvents.map((ev, i) => (
                <button
                  key={ev.event_id ?? ev.id ?? i}
                  type="button"
                  aria-label={`Show event ${i + 1} of ${liveEvents.length}`}
                  aria-current={i === idx}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className="h-1.5 rounded-full transition-all"
                  style={{ width: i === idx ? 16 : 6, backgroundColor: i === idx ? accent : `${accent}55` }}
                />
              ))}
            </div>
            <button
              type="button"
              aria-label="Next event"
              onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % liveEvents.length); }}
              className="absolute right-1.5 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-surface/90 shadow-sm ring-1 ring-black/5 backdrop-blur transition hover:scale-105 hover:shadow active:scale-95"
              style={{ color: accent }}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
      {modalOpen && <EventDetailModal e={event} primary={primary} tz={tz} onClose={() => setModalOpen(false)} />}

      {/* ═══ Mobile (<md): compact in-page event banner (no floating bubble) ═══ */}
      <div className={`md:hidden ${className}`}>
        <div
          onClick={() => setModalOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setModalOpen(true)}
          className="group relative cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition active:scale-[0.99]"
          style={{ borderColor: `${accent}33`, background: `linear-gradient(115deg, ${accent}1f 0%, rgb(var(--c-surface)) 62%)` }}
        >
          {/* Alert accent rail */}
          <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: accent }} />
          <span className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
          <div className={`relative flex items-center gap-3 py-2.5 pl-4 pr-2.5 ${liveEvents.length > 1 ? "pb-4" : ""}`}>
            {/* Date chip (or event thumbnail when there's an image) */}
            {event.image ? (
              <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl shadow ring-1 ring-black/5">
                <img src={event.image} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                <span className="absolute inset-x-0 bottom-0 bg-black/45 py-px text-center text-[9px] font-extrabold leading-tight text-white">{day || "—"}</span>
              </div>
            ) : (
              <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl text-center text-white shadow ring-1 ring-white/30" style={{ background: `linear-gradient(160deg, ${accent} 0%, ${accent}d9 100%)` }}>
                <span className="text-base font-extrabold leading-none">{day || "—"}</span>
                {mon && <span className="text-[7px] font-bold tracking-[0.1em] opacity-90">{mon}</span>}
              </div>
            )}

            {/* Details — upcoming tag + title + date, tight two lines */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: accent }}>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: accent }} />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
                  </span>
                  {phaseLabel}
                </span>
                {online && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-green-50 dark:bg-green-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-green-600 dark:text-green-300">
                    <svg className="h-2 w-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.55-2.28A1 1 0 0121 8.6v6.8a1 1 0 01-1.45.88L15 14M5 6h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z" />
                    </svg>
                    Live
                  </span>
                )}
              </div>
              <h3 className="mt-0.5 truncate text-[13.5px] font-bold leading-snug text-gray-900">{event.title}</h3>
              <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] font-medium text-gray-500">
                <svg className="h-3 w-3 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
                </svg>
                <span className="truncate">{lo.dateLabel}{lo.timeLabel ? ` · ${lo.timeLabel}` : ""}</span>
              </div>
            </div>

            {/* Chevron affordance */}
            <svg className="h-5 w-5 shrink-0 text-gray-300 dark:text-gray-500 transition group-active:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>

          {/* Carousel dots when there are several upcoming events */}
          {liveEvents.length > 1 && (
            <div className="absolute bottom-1.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5">
              {liveEvents.map((ev, i) => (
                <button
                  key={ev.event_id ?? ev.id ?? i}
                  type="button"
                  aria-label={`Show event ${i + 1} of ${liveEvents.length}`}
                  aria-current={i === idx}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className="h-1.5 rounded-full transition-all"
                  style={{ width: i === idx ? 16 : 6, backgroundColor: i === idx ? accent : `${accent}55` }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function EventsView({ token, groupToken, primary, member, groupCard, template = "classic", communityName = "" }) {
  // Spotlight lays the page out open (title, then the schedule) beside the
  // rail, instead of inside one card — the same shape as its Courses and
  // Community pages.
  const isSpotlight = template === "spotlight";
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [tz, setTz] = useState(""); // community timezone (from API)
  const [view, setView] = useState("list"); // "calendar" | "list" — default to the event-card list
  const [phaseFilter, setPhaseFilter] = useState("all"); // "all" | "live" | "upcoming" | "past"
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  // Close the filter menu on an outside click, the way every other menu here does.
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (ev) => {
      if (filterRef.current && !filterRef.current.contains(ev.target)) setFilterOpen(false);
    };
    addOutsidePress(onDown);
    return () => removeOutsidePress(onDown);
  }, [filterOpen]);
  // Ticks so an event crossing into (or out of) "live" re-groups itself while the
  // page is open, instead of waiting for a navigation.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const [cursor, setCursor] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [selected, setSelected] = useState(null);
  // Day picked in the month grid (YYYY-MM-DD) — opens the agenda under it.
  const [selectedDay, setSelectedDay] = useState(null);
  const { me, pts } = useMyPoints(token, groupToken, member);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .post(`${config.COMMUNITY_API_URL}/membership-events`, {
        community_token: token,
        group_token: groupToken,
      })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setTz(res.data.timezone || "");
        // Re-derive `upcoming` from the event's own wall clock before anything
        // downstream reads it. The server sets that flag by comparing a UTC
        // "now" against a stored time that carries no timezone (see
        // toZonedEvent), so it can be a whole UTC offset out — an event that
        // finished an hour ago still arrives flagged upcoming. Normalising once
        // here fixes every consumer (the two lists, the calendar dots, the
        // "· Ended" suffix) instead of each having to second-guess the flag.
        const zone = res.data.timezone || "";
        const now = Date.now();
        setEvents(
          [...(res.data.upcoming || []), ...(res.data.past || [])].map((e) => {
            const end = toZonedEvent(e, zone).endMs;
            return end == null ? e : { ...e, upcoming: end > now };
          })
        );
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken]);

  // Events keyed by their date in the COMMUNITY timezone for cell lookup.
  const byDate = useMemo(() => {
    const m = {};
    for (const e of events) {
      const key = toZonedEvent(e, tz).localIso;
      if (!key) continue;
      (m[key] ||= []).push(e);
    }
    return m;
  }, [events, tz]);

  const today = new Date();
  // Viewer-local: the calendar groups events by their local date now.
  const todayIso = localIso(today);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthName = cursor.toLocaleDateString(undefined, { month: "long" });
  // The reader's own clock, matching the times on the cards below it.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const tzCity = zone.split("/").pop().replace(/_/g, " ");
  const nowLabel = fmtTime(today.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }));

  // 6-week (or fewer) Monday-first grid for the visible month.
  const cells = useMemo(() => {
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = Math.ceil((startOffset + daysInMonth) / 7);
    return Array.from({ length: weeks * 7 }, (_, i) => new Date(year, month, 1 - startOffset + i));
  }, [year, month]);

  // Three groups, not two. The API only says upcoming/past, so an event that has
  // STARTED but not finished arrived as "upcoming" and sat in that list with no
  // hint it was running right now. Phase comes from the same wall clock the cards
  // print (see eventPhase), so the heading, the count and the card badge agree.
  const grouped = useMemo(() => {
    const live = [];
    const soon = [];
    const done = [];
    events.forEach((e) => {
      const phase = eventPhase(toZonedEvent(e, tz), nowMs);
      (phase === "live" ? live : phase === "ended" ? done : soon).push(e);
    });
    return { live, soon, done };
  }, [events, tz, nowMs]);
  const live = grouped.live;
  const upcoming = grouped.soon;
  const past = grouped.done;

  // Count pills double as filters — with a live event mixed into a long list,
  // "show me only what's on now" is the first thing anyone reaches for.
  const shownLive = phaseFilter === "all" || phaseFilter === "live" ? live : [];
  const shownUpcoming = phaseFilter === "all" || phaseFilter === "upcoming" ? upcoming : [];
  const shownPast = phaseFilter === "all" || phaseFilter === "past" ? past : [];

  // Live and Past are offered only when there is something in them — a menu row
  // that always reads "0" is a dead option.
  const filterOptions = [
    { key: "all", label: "All events", count: live.length + upcoming.length + past.length },
    ...(live.length ? [{ key: "live", label: "Live now", count: live.length, dot: true }] : []),
    { key: "upcoming", label: "Upcoming", count: upcoming.length },
    ...(past.length ? [{ key: "past", label: "Past", count: past.length }] : []),
  ];
  const activeFilter = filterOptions.find((o) => o.key === phaseFilter) || filterOptions[0];

  // Events falling inside the visible month (community-tz keys), for the month
  // pill and the "nothing this month" hint.
  const monthEvents = useMemo(
    () =>
      events.filter((e) => {
        const k = toZonedEvent(e, tz).localIso;
        if (!k) return false;
        const [y, m] = k.split("-").map(Number);
        return y === year && m === month + 1;
      }),
    [events, tz, year, month]
  );

  // Soonest upcoming event — powers the "jump to next event" shortcut shown when
  // the month the user is browsing is empty.
  const nextEvent = useMemo(() => {
    const withKey = events
      .filter((e) => e.upcoming)
      .map((e) => ({ e, k: toZonedEvent(e, tz).localIso }))
      .filter((x) => x.k)
      .sort((a, b) => a.k.localeCompare(b.k));
    return withKey[0] || null;
  }, [events, tz]);

  const goMonth = (delta) => {
    setCursor(new Date(year, month + delta, 1));
    setSelectedDay(null);
  };
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDay(view === "calendar" ? todayIso : null);
  };
  // Jump the grid to a specific YYYY-MM-DD and highlight that day.
  const jumpTo = (iso) => {
    const [y, m] = iso.split("-").map(Number);
    setView("calendar");
    setCursor(new Date(y, m - 1, 1));
    setSelectedDay(iso);
  };

  const ToggleBtn = ({ mode, label, d }) => {
    const active = view === mode;
    return (
      <button
        onClick={() => setView(mode)}
        aria-label={`${label} view`}
        aria-pressed={active}
        className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold transition ${
          active ? "bg-surface shadow-sm" : "text-gray-500 hover:text-gray-700"
        }`}
        style={active ? { color: primary } : undefined}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.9">
          <path strokeLinecap="round" strokeLinejoin="round" d={d} />
        </svg>
        <span className="hidden sm:inline">{label}</span>
      </button>
    );
  };

  const header = (
    <div
      className={isSpotlight ? "mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2.5" : "flex flex-wrap items-center justify-between gap-x-3 gap-y-2.5 border-b border-gray-100 px-4 py-3 sm:px-5"}
      style={isSpotlight ? undefined : { background: `linear-gradient(180deg, ${primary}0a 0%, transparent 100%)` }}
    >
      {view === "calendar" ? (
        <div className="flex min-w-0 items-center gap-2.5">
          {/* Month stepper — paired chevrons read as one control. */}
          <div className="flex shrink-0 items-center rounded-xl border border-gray-200 bg-surface p-0.5 shadow-sm">
            <button
              onClick={() => goMonth(-1)}
              aria-label="Previous month"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-50 hover:text-gray-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <span className="h-4 w-px bg-gray-200" />
            <button
              onClick={() => goMonth(1)}
              aria-label="Next month"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-50 hover:text-gray-700"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5 leading-none">
              <span className="truncate text-[17px] font-extrabold tracking-tight text-gray-900">{monthName}</span>
              <span className="text-[15px] font-semibold text-gray-400">{year}</span>
            </div>
            <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-gray-400">
              <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 7.5V12l2.5 1.5" />
              </svg>
              <span className="truncate">{nowLabel} · {tzCity} time</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
          {/* One dropdown rather than a row of chips: it matches the Today /
              List / Month buttons it sits opposite, and on mobile it stays a
              single full-width control instead of a strip that has to scroll. */}
          {loading ? (
            <span className="text-[15px] font-extrabold tracking-tight text-gray-900">All events</span>
          ) : (
            <div ref={filterRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setFilterOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={filterOpen}
                className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-surface px-3 py-2 text-[13px] font-bold shadow-sm transition sm:w-auto sm:justify-start sm:py-1.5 sm:text-xs ${
                  filterOpen || phaseFilter !== "all"
                    ? "border-transparent"
                    : "border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
                style={
                  filterOpen || phaseFilter !== "all"
                    ? { backgroundColor: `${primary}14`, color: primary, borderColor: `${primary}59` }
                    : undefined
                }
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16M7 12h10M10 19h4" />
                  </svg>
                  <span className="truncate">{activeFilter.label}</span>
                  <span
                    className="shrink-0 rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums"
                    style={{ backgroundColor: `${primary}1f`, color: primary }}
                  >
                    {activeFilter.count}
                  </span>
                </span>
                <svg
                  className={`h-3.5 w-3.5 shrink-0 transition-transform ${filterOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                </svg>
              </button>

              {filterOpen && (
                <div
                  role="listbox"
                  className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-xl border border-gray-200 bg-surface p-1 shadow-[0_16px_40px_-12px_rgba(16,24,40,0.28)] sm:right-auto sm:w-56"
                >
                  {filterOptions.map((o) => {
                    const active = phaseFilter === o.key;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          setPhaseFilter(o.key);
                          setFilterOpen(false);
                        }}
                        className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition ${
                          active ? "" : "text-gray-700 hover:bg-gray-50"
                        }`}
                        style={active ? { backgroundColor: `${primary}14`, color: primary } : undefined}
                      >
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          {active ? (
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
                            </svg>
                          ) : o.dot ? (
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: primary }} />
                              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: primary }} />
                            </span>
                          ) : null}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{o.label}</span>
                        <span
                          className="shrink-0 rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums"
                          style={active ? { backgroundColor: `${primary}1f`, color: primary } : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-500))" }}
                        >
                          {o.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {view === "calendar" && monthEvents.length > 0 && (
          <span
            className="hidden items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold sm:inline-flex"
            style={{ backgroundColor: `${primary}14`, color: primary }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: primary }} />
            {monthEvents.length} this month
          </span>
        )}
        <button
          onClick={goToday}
          className="rounded-xl border border-gray-200 bg-surface px-3 py-1.5 text-xs font-bold text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          Today
        </button>
        <div className="inline-flex items-center rounded-xl bg-gray-100 p-0.5">
          <ToggleBtn mode="list" label="List" d="M4 6h16M4 12h16M4 18h16" />
          <ToggleBtn mode="calendar" label="Month" d="M8 7V3m8 4V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
        </div>
      </div>
    </div>
  );

  const selectedEvents = selectedDay ? byDate[selectedDay] || [] : [];

  const calendar = (
    <div className={isSpotlight ? "" : "px-3 pb-4 pt-3 sm:px-5 sm:pb-5"}>
      <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50/80">
          {WEEKDAYS.map((w, i) => (
            <div
              key={w}
              className={`py-2.5 text-center text-[10px] font-extrabold uppercase tracking-[0.14em] ${
                i > 4 ? "text-gray-300 dark:text-gray-500" : "text-gray-400"
              }`}
            >
              <span className="hidden sm:inline">{w}</span>
              <span className="sm:hidden">{w.charAt(0)}</span>
            </div>
          ))}
        </div>
        {/* Day cells — 1px hairline grid via gap on a gray backdrop. */}
        <div key={`${year}-${month}`} className="cal-grid grid grid-cols-7 gap-px bg-gray-200/70">
          {cells.map((d, i) => {
            const inMonth = d.getMonth() === month;
            const key = localIso(d);
            const dayEvents = byDate[key] || [];
            const isToday = key === todayIso;
            const isSel = key === selectedDay;
            const isWeekend = i % 7 > 4;
            const shown = dayEvents.slice(0, 2);
            const more = dayEvents.length - shown.length;
            return (
              <div
                key={i}
                role="button"
                tabIndex={0}
                aria-label={`${d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}, ${dayEvents.length} events`}
                onClick={() => setSelectedDay(isSel ? null : key)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    setSelectedDay(isSel ? null : key);
                  }
                }}
                className={`relative flex min-h-[86px] cursor-pointer flex-col gap-1 p-1.5 outline-none transition sm:min-h-[116px] ${
                  inMonth ? (isWeekend ? "bg-gray-50/50" : "bg-surface") : "bg-gray-50/80"
                } hover:bg-gray-50`}
                style={isSel ? { backgroundColor: `${primary}0d`, boxShadow: `inset 0 0 0 2px ${primary}` } : undefined}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1 text-[12px] font-bold tabular-nums transition ${
                      isToday ? "text-white" : inMonth ? "text-gray-700" : "text-gray-300 dark:text-gray-500"
                    }`}
                    style={isToday ? { backgroundColor: primary, boxShadow: `0 4px 10px -3px ${primary}` } : undefined}
                  >
                    {d.getDate()}
                  </span>
                  {/* Mobile: dots stand in for the (too narrow) chips. */}
                  {dayEvents.length > 0 && (
                    <span className="flex items-center gap-0.5 pr-0.5 sm:hidden">
                      {dayEvents.slice(0, 3).map((e) => (
                        <span
                          key={e.id}
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: e.upcoming ? primary : "#d1d5db" }}
                        />
                      ))}
                    </span>
                  )}
                </div>
                <div className="hidden min-w-0 flex-1 flex-col gap-1 sm:flex">
                  {shown.map((e) => (
                    <CalEventChip key={e.id} e={e} tz={tz} primary={primary} onOpen={setSelected} />
                  ))}
                  {more > 0 && (
                    <span className="px-1 text-[10px] font-bold text-gray-400">+{more} more</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Day agenda — opens under the grid when a date is picked. */}
      {selectedDay && (
        <div className="cal-panel mt-3 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-b from-gray-50/80 to-surface">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <span className="text-[13px] font-extrabold text-gray-900">
                {new Date(`${selectedDay}T00:00:00`).toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              {selectedDay === todayIso && (
                <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  Today
                </span>
              )}
            </div>
            <button
              onClick={() => setSelectedDay(null)}
              aria-label="Close day"
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4"><path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>
          {selectedEvents.length === 0 ? (
            <p className="px-4 py-5 text-center text-xs font-medium text-gray-400">Nothing scheduled on this day.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {selectedEvents.map((e) => {
                const lo = toZonedEvent(e, tz);
                const online = (e.event_type || 1) <= 3;
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => setSelected(e)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50/80"
                    >
                      <span
                        className="flex h-11 w-14 shrink-0 flex-col items-center justify-center rounded-xl text-[11px] font-extrabold leading-tight"
                        style={
                          e.upcoming
                            ? { backgroundColor: `${primary}14`, color: primary }
                            : { backgroundColor: "rgb(var(--c-gray-100))", color: "rgb(var(--c-gray-400))" }
                        }
                      >
                        {lo.timeLabel ? (
                          <>
                            <span className="tabular-nums">{fmtTime(lo.timeLabel).replace(/(am|pm)$/, "")}</span>
                            <span className="text-[9px] uppercase opacity-70">{fmtTime(lo.timeLabel).slice(-2)}</span>
                          </>
                        ) : (
                          <span className="text-[10px] uppercase">All day</span>
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-bold text-gray-900">{e.title}</span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-500">{online ? "Online" : "In person"}</span>
                          {e.location && <span className="truncate">{e.location}</span>}
                          {!e.upcoming && <span className="text-gray-300 dark:text-gray-500">· Ended</span>}
                        </span>
                      </span>
                      <svg className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Empty month → offer a one-click jump to the next scheduled event. */}
      {!selectedDay && monthEvents.length === 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 px-4 py-3 text-center">
          <span className="text-xs font-semibold text-gray-400">Nothing scheduled in {monthName}.</span>
          {nextEvent && (
            <button
              onClick={() => jumpTo(nextEvent.k)}
              className="inline-flex items-center gap-1 text-xs font-bold transition hover:gap-1.5"
              style={{ color: primary }}
            >
              Jump to {toZonedEvent(nextEvent.e, tz).dateLabel}
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
        </div>
      )}
    </div>
  );

  const list =
    live.length === 0 && upcoming.length === 0 && past.length === 0 ? (
      <EmptyState art="events" accent={primary} title="No events scheduled" message="Live sessions, calls and meet-ups for this membership land here. When one is scheduled you will see the date, the countdown and a way to join." />
    ) : shownLive.length === 0 && shownUpcoming.length === 0 && shownPast.length === 0 ? (
      /* Events exist — the phase filter is hiding them, so hand back the way out. */
      <EmptyState
        art="search"
        accent={primary}
        title={phaseFilter === "live" ? "Nothing live right now" : phaseFilter === "upcoming" ? "Nothing coming up" : "Nothing in the past"}
        message={phaseFilter === "live"
          ? "No session is running at this moment. Switch to Upcoming to see what is next."
          : phaseFilter === "upcoming"
            ? "Nothing is on the calendar ahead. Past events are still here to catch up on."
            : "There is no history yet — this membership's events are all still to come."}
        action={{ label: "Show all events", onClick: () => setPhaseFilter("all") }}
      />
    ) : (
      <div className={isSpotlight ? "space-y-8" : "p-4 sm:p-5 space-y-6"}>
        {shownLive.length > 0 && (
          <div>
            {/* Its own section, above Upcoming: an event happening right now is
                the one thing on this page that is time-critical. */}
            <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-900">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ background: primary }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: primary }} />
              </span>
              Happening now <span className="text-gray-400">({live.length})</span>
            </h3>
            <div className="space-y-3">
              {shownLive.map((e) => (
                <EventCard key={e.id} e={e} primary={primary} tz={tz} live />
              ))}
            </div>
          </div>
        )}
        {shownUpcoming.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-bold text-gray-900">Upcoming events <span className="text-gray-400">({upcoming.length})</span></h3>
            <div className="space-y-3">
              {shownUpcoming.map((e) => (
                <EventCard key={e.id} e={e} primary={primary} tz={tz} />
              ))}
            </div>
          </div>
        )}
        {shownPast.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-bold text-gray-900">Past events <span className="text-gray-400">({past.length})</span></h3>
            <div className="space-y-3">
              {shownPast.map((e) => (
                <EventCard key={e.id} e={e} primary={primary} past tz={tz} />
              ))}
            </div>
          </div>
        )}
      </div>
    );

  const body = (
    <div className="space-y-5">
      {/* Members-style heading band at the top of the card. */}
      <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <SectionHeader
          primary={primary}
          iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          title="Events"
          subtitle="Upcoming & past events"
          trailing={loading ? "…" : `${events.length} ${events.length === 1 ? "event" : "events"}`}
        />
        {header}
        {loading ? (
          <EventListSkeleton />
        ) : view === "calendar" ? (
          calendar
        ) : (
          list
        )}
      </div>
    </div>
  );

  const rail = groupCard ? (
    <>
      {groupCard}
      <MyPointsCard me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
      <InlineBanner token={token} groupToken={groupToken} placement="events" wrapperClass="space-y-3" />
    </>
  ) : null;

  const wrapped = isSpotlight ? (
    <SpotlightRailLayout rail={rail}>
      <SpotlightIntro
        title="Events"
        subtitle={`Live sessions, workshops and meet-ups${communityName ? ` in ${communityName}` : ""} — see what's on and save your seat.`}
      />
      <SpotlightExplore
        title="Schedule"
        caption={loading ? "Loading events…" : `${events.length} ${events.length === 1 ? "event" : "events"} · shown in your time zone`}
      >
        {header}
        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-surface">
            <EventListSkeleton />
          </div>
        ) : view === "calendar" ? (
          calendar
        ) : shownLive.length + shownUpcoming.length + shownPast.length === 0 ? (
          // An empty state needs a surface to sit on; event cards don't.
          <div className="rounded-2xl border border-gray-200 bg-surface">{list}</div>
        ) : (
          list
        )}
      </SpotlightExplore>
    </SpotlightRailLayout>
  ) : !groupCard ? (
    body
  ) : (
    <div className="tpl-rail-grid">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className=" space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
        {groupCard}
        <MyPointsCard me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
        <InlineBanner token={token} groupToken={groupToken} placement="events" wrapperClass="space-y-3" />
      </aside>
      <div className=" order-1">{body}</div>
    </div>
  );

  return (
    <>
      {wrapped}
      <EventDetailModal e={selected} primary={primary} tz={tz} onClose={() => setSelected(null)} />
    </>
  );
}

/** Left-sidebar "My Badges" card — earned badges highlighted, locked ones dimmed. */
function MyBadgesCard({ badges = [], primary }) {
  const earnedCount = badges.filter((b) => b.earned).length;
  return (
    <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* The house header, same as Rewards and Leaderboard above it. It used to be
          hand-rolled here with a bare w-4 h-4 shield and no shrink-0 — in a flex
          row that let the icon be squeezed to a sliver, which is what it looked
          like next to a full-width title. */}
      <SectionHeader
        primary={primary}
        /* The admin's Gamification modal names this section with Heroicons'
           CheckBadgeIcon (GamificationModal.jsx: { key: "badges", Icon:
           CheckBadgeIcon }). Same icon here, so a creator setting badges up and
           a member collecting them are looking at the same mark. */
        icon={<CheckBadgeIcon className="h-[26px] w-[26px]" strokeWidth={1.7} />}
        title="My Badges"
        subtitle="Milestones you unlock as you take part"
        trailing={badges.length > 0 ? `${earnedCount}/${badges.length}` : null}
      />
      {badges.length === 0 ? (
        <EmptyState compact art="badges" accent={primary} title="No badges yet" message="Badges are the milestones this membership hands out - for streaks, for finishing a course, for showing up. The ones you earn will be collected here." />
      ) : (
        <div className="p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {badges.map((b) => (
            <div
              key={b.id}
              title={b.earned ? b.name : `${b.name} — ${b.points} pts to unlock`}
              className="flex flex-col items-center text-center bg-gray-50/60 rounded-xl border border-gray-100 p-3"
            >
              <div className="relative">
                <span
                  className="block rounded-full p-[3px] shadow-sm"
                  style={{ background: b.earned ? RANK_MEDAL_BG[1] : "rgb(var(--c-gray-200))" }}
                >
                  <span className="w-14 h-14 rounded-full bg-surface ring-2 ring-surface flex items-center justify-center overflow-hidden">
                    <img
                      src={b.image || DEFAULT_BADGE_IMAGE}
                      alt={b.name}
                      className={`w-full h-full object-contain p-1 ${b.earned ? "" : "grayscale opacity-50"}`}
                    />
                  </span>
                </span>
                <span
                  className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white ring-2 ring-surface ${
                    b.earned ? "bg-green-500" : "bg-gray-400"
                  }`}
                >
                  {b.earned ? (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
                    </svg>
                  )}
                </span>
              </div>
              <p className="mt-2 text-xs font-semibold text-gray-700 truncate w-full">{b.name}</p>
              <span
                // The earned state is a fixed success tint, so it is expressed
                // as classes (which can carry a dark twin) rather than an inline
                // style; the unearned state is brand-tinted and stays inline.
                className={`mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${
                  b.earned ? "bg-green-100 dark:bg-green-500/15 text-green-600 dark:text-green-300" : ""
                }`}
                style={b.earned ? undefined : { backgroundColor: `${primary}14`, color: primary }}
              >
                {b.earned ? "Earned" : `${b.points} pts`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Rewards-page "My Points" — same premium card as everywhere else (kept as a thin
    alias so the design stays consistent across all pages). */
function MyPointsPanel(props) {
  return <MyPointsCard {...props} />;
}


// Reward type labels — mirror REWARD_TYPES in the admin's GamificationModal.jsx
// so a member sees the same wording the creator picked.
// 1 = Instructions, 2 = External URL, 3 = Video, 4 = Audio, 5 = Upload File.
const REWARD_TYPE_LABEL = { 1: "Instructions", 2: "External URL", 3: "Video", 4: "Audio", 5: "Downloadable file" };

/** Host of a URL ("https://www.instagram.com/x" -> "instagram.com"), for link previews. */
function linkHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * Reward video player — a fixed 16:9 stage so the frame never collapses or
 * overflows the popup, whatever the source. The caption strip underneath names
 * the host and offers "Open in new tab", which is the way out when a host
 * refuses to be embedded (the player would otherwise just sit there blank).
 */
function VideoPlayer({ url, primary }) {
  const e = toEmbed(url);
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
      <div className="relative w-full bg-black" style={{ paddingTop: "56.25%" }}>
        {e.kind === "video" ? (
          <video
            src={e.src}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full bg-black"
          />
        ) : (
          <iframe
            src={e.src}
            title="Reward video"
            className="absolute inset-0 h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-surface px-4 py-2.5">
        <p className="truncate text-xs text-gray-500">{linkHost(url)}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold hover:underline"
          style={{ color: primary }}
        >
          Open in new tab
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5M19 5l-8 8M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" />
          </svg>
        </a>
      </div>
    </div>
  );
}

/**
 * Resolve an audio URL the same way toEmbed() resolves video: a direct file plays
 * natively, the streaming hosts a creator is likely to paste get their real player,
 * and anything else is attempted natively with the caption's "Open in new tab" as
 * the way out.
 */
function toAudioEmbed(url) {
  const u = String(url || "").trim();

  if (/\.(mp3|wav|m4a|aac|oga|ogg|flac|opus|weba)(\?|$)/i.test(u)) {
    return { kind: "audio", src: u };
  }

  const spotify = u.match(/open\.spotify\.com\/(track|episode|album|playlist|show)\/([\w-]+)/);
  if (spotify) return { kind: "iframe", src: `https://open.spotify.com/embed/${spotify[1]}/${spotify[2]}`, height: 152 };

  if (/soundcloud\.com\//i.test(u)) {
    return { kind: "iframe", src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(u)}`, height: 166 };
  }

  const drive = u.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (drive) return { kind: "iframe", src: `https://drive.google.com/file/d/${drive[1]}/preview`, height: 80 };

  return { kind: "audio", src: u };
}

/**
 * Reward audio player — the same card shape as VideoPlayer so the two reward
 * types read as one family: a branded stage, then a caption strip naming the host
 * with an "Open in new tab" escape hatch. That hatch is what saves a reward whose
 * URL the browser can't decode natively (a page link rather than a media file),
 * which otherwise renders as a dead player with no explanation.
 */
function AudioPlayer({ url, primary }) {
  const e = toAudioEmbed(url);
  const [failed, setFailed] = useState(false);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
      <div
        className="px-4 pb-4 pt-3.5"
        style={{ background: `linear-gradient(135deg, ${primary}14 0%, ${primary}08 60%, rgb(var(--c-surface)) 100%)` }}
      >
        <div className="mb-3 flex items-center gap-3">
          <RewardTile primary={primary}>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 18V5l12-2v13M9 18a3 3 0 11-6 0 3 3 0 016 0zm12-2a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </RewardTile>
          <p className="text-sm font-semibold text-gray-800">Audio reward</p>
        </div>

        {e.kind === "iframe" ? (
          <iframe
            src={e.src}
            title="Reward audio"
            className="w-full rounded-xl"
            style={{ height: e.height || 152 }}
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : failed ? (
          <p className="rounded-xl bg-surface/70 px-3.5 py-3 text-[13px] text-gray-500 ring-1 ring-black/5">
            This audio can&rsquo;t be played here — open it in a new tab below.
          </p>
        ) : (
          <audio
            src={e.src}
            controls
            preload="metadata"
            onError={() => setFailed(true)}
            className="w-full"
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-surface px-4 py-2.5">
        <p className="truncate text-xs text-gray-500">{linkHost(url)}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold hover:underline"
          style={{ color: primary }}
        >
          Open in new tab
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5M19 5l-8 8M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" />
          </svg>
        </a>
      </div>
    </div>
  );
}

/**
 * Text clamped to `lines`, with a Read more / Read less toggle that only appears
 * when the text actually overflows — so a one-line description stays clean while
 * a long one no longer stretches the popup.
 *
 * `overflows` is sticky once true: it's measured while collapsed, and re-measuring
 * after expanding would (correctly) report no overflow and hide the toggle,
 * stranding the reader in the expanded state.
 */
function ClampText({ text, lines = 3, className = "", primary }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Collapsing a very long block leaves the reader parked wherever the removed
  // text used to be, so pull the block back into view.
  const toggle = () => {
    setExpanded((v) => {
      if (v) wrapRef.current?.scrollIntoView({ block: "nearest" });
      return !v;
    });
  };

  useEffect(() => {
    setExpanded(false);
    setOverflows(false);
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setOverflows((prev) => prev || el.scrollHeight - el.clientHeight > 2);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, lines]);

  const clamp = {
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  return (
    <div ref={wrapRef}>
      <p ref={ref} className={className} style={expanded ? undefined : clamp}>
        {text}
      </p>
      {overflows && (
        <button
          type="button"
          onClick={toggle}
          className="mt-1 inline-flex items-center gap-1 text-[12px] font-semibold hover:underline"
          style={{ color: primary }}
        >
          {expanded ? "Read less" : "Read more"}
          <svg
            className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
}

/** Small tinted icon tile used by the reward popup's content rows. */
function RewardTile({ primary, children }) {
  return (
    <span
      className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
      style={{ backgroundColor: `${primary}14`, color: primary }}
    >
      {children}
    </span>
  );
}

/**
 * The unlocked reward's payload, rendered per type. Types 2 (external URL) and
 * 5 (file) show a preview row here and put their real action in the popup's
 * footer, so every reward has its call-to-action in the same place.
 */
function RewardValue({ type, value, primary }) {
  if (!value) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 px-5 py-8 text-center">
        <p className="text-sm font-medium text-gray-500">Nothing here yet</p>
        <p className="mt-1 text-xs text-gray-400">The creator hasn't added the content for this reward.</p>
      </div>
    );
  }

  // Instructions — the reward IS this text, so give it room and a copy action.
  if (type === 1) {
    return <InstructionsPanel text={value} primary={primary} />;
  }

  if (type === 3) {
    return <VideoPlayer url={value} primary={primary} />;
  }

  if (type === 4) {
    return <AudioPlayer url={value} primary={primary} />;
  }

  // 5 = hosted file
  if (type === 5) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
        <RewardTile primary={primary}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
          </svg>
        </RewardTile>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800">Your file is ready</p>
          <p className="truncate text-xs text-gray-500">{value.split("/").pop() || value}</p>
        </div>
      </div>
    );
  }

  // 2 = external URL
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
      <RewardTile primary={primary}>
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
        </svg>
      </RewardTile>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800">You've unlocked a link</p>
        <p className="truncate text-xs text-gray-500">{linkHost(value)}</p>
      </div>
    </div>
  );
}

/** Instructions body with a copy-to-clipboard action. */
function InstructionsPanel({ text, primary }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      },
      () => {}
    );
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/70 px-4 py-2.5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">Instructions</p>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold transition hover:bg-surface"
          style={{ color: primary }}
        >
          {copied ? (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 012-2h10" />
            </svg>
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {/* Clamped rather than an inner scrollbar — a scroll area nested inside the
          popup's own scroll is awkward to use on touch. */}
      <div className="px-4 py-4">
        <ClampText
          text={text}
          lines={10}
          primary={primary}
          className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700"
        />
      </div>
    </div>
  );
}

/**
 * Reward popup — light, premium sheet: tinted header with the reward's artwork,
 * name, type and points, a type-aware body, and one primary action in the footer
 * carrying the creator's own button label.
 */
function RewardModal({ reward, primary, onClose }) {
  useEffect(() => {
    if (!reward) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [reward, onClose]);

  if (!reward) return null;

  const type = Number(reward.type) || 1;
  const label = reward.btn_name || "Get Access";
  // Only a link and a file have somewhere to send the member; instructions,
  // video and audio are consumed in the popup itself.
  const action = reward.earned && reward.value && (type === 2 || type === 5);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-ink/50 backdrop-blur-sm"
      /* No backdrop-click close — a stray click on the dim area must not
         dismiss a reward the member just unlocked. Close via the X or Close. */
    >
      {/* Video gets a wider sheet — a 16:9 player in the narrow one is a letterbox. */}
      <div
        className={`flex h-full w-full flex-col overflow-hidden rounded-none bg-surface shadow-2xl sm:h-auto sm:max-h-[92vh] sm:rounded-3xl sm:ring-1 sm:ring-black/5 ${
          type === 3 ? "sm:max-w-2xl" : "sm:max-w-lg"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Light header: a soft wash of the community colour, dark type ── */}
        <div
          className="relative shrink-0 border-b border-gray-100 px-5 pb-5 pt-5 sm:px-6"
          style={{ background: `linear-gradient(160deg, ${primary}1A 0%, ${primary}08 45%, rgb(var(--c-surface)) 100%)` }}
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3.5 top-3.5 grid h-8 w-8 place-items-center rounded-full bg-surface/70 text-gray-400 shadow-sm ring-1 ring-black/5 transition hover:bg-surface hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>

          <div className="flex items-start gap-4 pr-9">
            <span className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-surface shadow-sm ring-1 ring-black/5">
              {reward.image ? (
                <img src={reward.image} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center" style={{ backgroundColor: `${primary}14`, color: primary }}>
                  <RewardGlyph type={type} className="h-7 w-7" />
                </span>
              )}
            </span>

            <div className="min-w-0 pt-0.5">
              <h3 className="truncate text-lg font-bold leading-tight text-gray-900">{reward.name || "Reward"}</h3>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span
                  className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  style={{ backgroundColor: `${primary}1A`, color: primary }}
                >
                  {REWARD_TYPE_LABEL[type] || "Reward"}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold text-amber-600 dark:text-amber-300">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l2.39 6.96H22l-6.2 4.51 2.37 7.03L12 16.9 5.83 20.5l2.37-7.03L2 8.96h7.61z" />
                  </svg>
                  {reward.points} {reward.points === 1 ? "point" : "points"}
                </span>
                {reward.earned && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-300">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Unlocked
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Body. The description lives HERE, not in the header: the header is
            shrink-0, so an expanded "Read more" grew it past the sheet and pushed
            the player, the footer and the Read-less button clean out of view. In
            the body it expands inside the scroll area, and it sits AFTER the
            payload so the video/link is what greets you. ── */}
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-6">
          {reward.earned ? (
            <RewardValue type={type} value={reward.value} primary={primary} />
          ) : (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-6 text-center">
              <span
                className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${primary}14`, color: primary }}
              >
                <LockIcon className="h-5 w-5" />
              </span>
              <p className="text-sm text-gray-600">
                Reach{" "}
                <span className="font-semibold" style={{ color: primary }}>
                  {reward.points} points
                </span>{" "}
                to unlock this reward.
              </p>
            </div>
          )}

          {reward.description ? (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                About this reward
              </p>
              <ClampText
                text={reward.description}
                lines={3}
                primary={primary}
                className="whitespace-pre-wrap text-[13px] leading-relaxed text-gray-600"
              />
            </div>
          ) : null}
        </div>

        {/* ── Footer: the creator's button label drives the one primary action ── */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/60 px-5 py-4 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-500 transition hover:text-gray-700"
          >
            Close
          </button>
          {action && (
            <a
              href={reward.value}
              target="_blank"
              rel="noopener noreferrer"
              {...(type === 5 ? { download: "" } : {})}
              onClick={onClose}
              style={{ backgroundColor: primary, boxShadow: `0 8px 20px -8px ${primary}99` }}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 active:scale-[0.98]"
            >
              {label}
              {type === 5 ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M12 4v12m0 0l-4-4m4 4l4-4" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5M19 5l-8 8M18 14v4a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h4" />
                </svg>
              )}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
function LockIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Type-aware glyph used inside the premium default reward banner. */
function RewardGlyph({ type, className = "w-8 h-8" }) {
  const common = { className, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  if (type === 3) {
    // video
    return (
      <svg {...common}>
        <rect x="2" y="5" width="14" height="14" rx="3" />
        <path d="M16 9l6-3v12l-6-3z" />
      </svg>
    );
  }
  if (type === 4) {
    // audio
    return (
      <svg {...common}>
        <path d="M9 18V6l10-2v12" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="16" cy="16" r="3" />
      </svg>
    );
  }
  // gift (instructions / external / file / default)
  return (
    <svg {...common}>
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
      <path d="M12 8C12 8 11 3 8 3a2.5 2.5 0 0 0 0 5h4zM12 8s1-5 4-5a2.5 2.5 0 0 1 0 5h-4z" />
    </svg>
  );
}

/**
 * Premium default artwork shown when a reward has no uploaded image: a branded
 * gradient in the community's primary colour with a soft glow, faint decorative
 * rings and a frosted-glass icon badge. Scales cleanly and stays on-brand.
 */
function RewardDefaultBanner({ primary, type }) {
  return (
    <div
      className="relative w-full h-full overflow-hidden"
      style={{ background: `linear-gradient(135deg, ${primary} 0%, #0f172a 100%)` }}
    >
      {/* soft brand-coloured glow */}
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(120% 90% at 25% 15%, ${primary}80, transparent 55%)` }}
      />
      {/* faint decorative rings */}
      <svg className="absolute -right-10 -bottom-12 w-48 h-48 text-white/10" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="100" cy="100" r="40" />
        <circle cx="100" cy="100" r="70" />
        <circle cx="100" cy="100" r="96" />
      </svg>
      {/* frosted-glass icon badge */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="h-16 w-16 rounded-2xl bg-white/10 ring-1 ring-white/25 backdrop-blur-sm shadow-lg flex items-center justify-center">
          <RewardGlyph type={type} className="w-8 h-8 text-white" />
        </div>
      </div>
    </div>
  );
}

/**
 * A single reward, styled to match the community group cards: banner with corner
 * badges → logo chip + title → 3-line description → "N Points • Unlocked/Locked"
 * meta footer. The whole card is the action target (like the button-less group
 * cards): earned rewards open the value popup. Locked rewards keep their full
 * colour and, on hover, reveal a full-card black overlay with the unlock message.
 */
function RewardCard({ reward: r, primary, onOpen }) {
  const locked = !r.earned;

  const open = () => !locked && onOpen(r);

  return (
    <div className="relative group">
      <div
        onClick={open}
        role="button"
        tabIndex={0}
        aria-disabled={locked}
        onKeyDown={(e) => {
          if (!locked && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            open();
          }
        }}
        className={`relative bg-surface rounded-2xl border border-gray-200 ring-1 ring-black/[0.03] shadow-sm overflow-hidden transition flex flex-col h-full ${
          locked ? "cursor-default" : "cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
        }`}
      >
        {/* compact banner */}
        <div className="h-20 bg-gray-100 overflow-hidden relative">
          {r.image ? (
            <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
          ) : (
            <RewardDefaultBanner primary={primary} type={r.type} />
          )}
          {/* status badge — Locked (solid dark) vs Unlocked (light pill) */}
          {locked ? (
            <span className="absolute top-1.5 right-1.5 text-[10px] font-semibold text-white bg-ink/80 backdrop-blur px-1.5 py-0.5 rounded-full flex items-center gap-1">
              <LockIcon className="w-2.5 h-2.5" />
              Locked
            </span>
          ) : (
            <span
              className="absolute top-1.5 right-1.5 text-[10px] font-semibold bg-surface/95 backdrop-blur px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
              style={{ color: primary }}
            >
              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 018 0M6 11h12v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8z" />
              </svg>
              Unlocked
            </span>
          )}
        </div>

        {/* body */}
        <div className="px-3 pb-3 pt-3 flex flex-col flex-1">
          <h3 className="font-bold text-gray-900 text-sm line-clamp-1">{r.name}</h3>

          {/* description — reserve 2 lines so cards line up */}
          <p className="text-xs text-gray-500 mt-1 line-clamp-2 min-h-[2rem]">{r.description || "—"}</p>

          {/* bottom row: points pill + compact claim button */}
          <div className="mt-auto pt-2.5 flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-0.5 shrink-0"
              style={{ backgroundColor: `${primary}14`, color: primary }}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l2.39 6.96H22l-6.2 4.51 2.37 7.03L12 16.9 5.83 20.5l2.37-7.03L2 8.96h7.61z" />
              </svg>
              {r.points}
            </span>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              disabled={locked}
              style={locked ? undefined : { backgroundColor: primary }}
              className={`inline-flex items-center justify-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition active:scale-[0.97] truncate ${
                locked ? "bg-gray-100 text-gray-400 cursor-default" : "text-white hover:opacity-90"
              }`}
            >
              {locked && <LockIcon className="w-3 h-3" />}
              {locked ? "Locked" : r.btn_name || "Claim"}
            </button>
          </div>
        </div>

        {/* Full-card hover overlay — only for locked rewards. */}
        {locked && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/70 px-4 text-center opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-white text-xs font-semibold leading-relaxed">
              Reach {r.points} points to unlock this reward.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function RewardsView({ token, groupToken, primary, member, groupCard, topBanner }) {
  const [loading, setLoading] = useState(true);
  const [badges, setBadges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [activeReward, setActiveReward] = useState(null);
  const { me, pts } = useMyPoints(token, groupToken, member);

  useEffect(() => {
    let active = true;
    setLoading(true);
    // The full badge + reward catalogue. This is the ONLY screen that needs it,
    // which is why it is its own route: the rail carries just the member's
    // earned badges, so no other page pays for this.
    cachedPost(`${config.COMMUNITY_API_URL}/membership-badge-reward-list`, {
        community_token: token,
        group_token: groupToken,
        community_member_id: member?.community_member_id || undefined,
        member_token: member?.auth_token,
      }, { ttl: 30000 })
      .then((res) => {
        if (!active || res.data?.error !== 0) return;
        setBadges(res.data.badges || []);
        setRewards(res.data.rewards || []);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token, groupToken, member?.community_member_id]);

  // Every earned reward opens the popup, whatever its type. Links and files used
  // to fire straight from the card, which left the creator's button label with
  // nowhere to appear and gave the member no idea what they were about to open —
  // the popup now presents the reward and carries that label on its one action.
  const handleReward = (r) => {
    if (!r.earned) return;
    setActiveReward(r);
  };

  return (
    <div className="tpl-rail-grid">
      {/* Group + My Points — right rail on desktop (sticky), stacks first on mobile */}
      <aside className=" space-y-4 order-2 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1 scrollbar-hide">
        {groupCard}
        <MyPointsPanel me={me} pts={pts} token={token} groupToken={groupToken} primary={primary} member={member} />
        <InlineBanner token={token} groupToken={groupToken} placement="rewards" wrapperClass="space-y-3" />
      </aside>

      {/* Rewards + My Badges — main column on the left on desktop */}
      <div className=" order-1 space-y-6">
        {topBanner}
        <div className="bg-surface rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <SectionHeader
            primary={primary}
            iconPath="M20 12v9H4v-9M2 7h20v5H2V7zm10 0v14M12 7S9 7 8 5.5 9 3 10 3.5 12 7 12 7zm0 0s3 0 4-1.5S15 3 14 3.5 12 7 12 7z"
            title="Rewards"
            subtitle="Unlock perks as you earn points"
            trailing={!loading && rewards.length > 0 ? `${rewards.length} total` : null}
          />
          {loading ? (
            <RewardGridSkeleton />
          ) : rewards.length === 0 ? (
            <EmptyState art="rewards" accent={primary} title="No rewards yet" message="Rewards are what points buy - discounts, downloads, a call, early access. When the creator sets them up they appear here with the points each one costs." />
          ) : (
            <div className="p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {rewards.map((r) => (
                <RewardCard key={r.id} reward={r} primary={primary} onOpen={handleReward} />
              ))}
            </div>
          )}
        </div>

        {/* My Badges — below the rewards collection in the main column */}
        <MyBadgesCard badges={badges} primary={primary} />
      </div>

      <RewardModal reward={activeReward} primary={primary} onClose={() => setActiveReward(null)} />
    </div>
  );
}
